import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Divider,
  TextField,
  CircularProgress,
  Chip,
  Tab,
  Tabs,
  Tooltip,
  Fade,
  IconButton,
  List as ListIcon,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Slide,
  Menu,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Visibility';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import ScheduleIcon from '@mui/icons-material/Schedule';
import StarIcon from '@mui/icons-material/Star';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SettingsOverscanIcon from '@mui/icons-material/SettingsOverscan';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import AddIcon from '@mui/icons-material/Add';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import TabletIcon from '@mui/icons-material/Tablet';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import LightbulbIcon from '@mui/icons-material/LightbulbOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CodeIcon from '@mui/icons-material/Code';
import Editor from '@monaco-editor/react';
import api from '../../../../api/client';
import RichTextEditor from '../../../LexicalRichText';
import { useTranslation } from 'react-i18next';
import { usePageLoading } from '../../../../contexts/LoadingContext';
import AptiviewLoader from '../../../common/AptiviewLoader/AptiviewLoader';
import PageHeader, { PageBody, PageCard, PageShell } from '../../../common/PageHeader';
import { toast } from 'react-toastify';
import DynamicBasicDialog from '../../component/DynamicBasicDialog';
import { useUser } from '../../../../contexts/userContext';

export const extractVisualContent = (rawHtml) => {
  if (!rawHtml || typeof rawHtml !== 'string') return '';
  let content = rawHtml;

  // 1. Extract content inside {% block content %} if present
  if (content.includes('{% block content %}')) {
    const startIdx = content.indexOf('{% block content %}') + '{% block content %}'.length;
    let endIdx = -1;
    if (content.includes('{% block footer_signature %}')) {
      endIdx = content.lastIndexOf('{% endblock %}', content.indexOf('{% block footer_signature %}'));
    } else {
      endIdx = content.lastIndexOf('{% endblock %}');
    }
    if (endIdx !== -1 && endIdx > startIdx) {
      content = content.substring(startIdx, endIdx);
    } else {
      content = content.substring(startIdx);
    }
  } else {
    // Strip other known blocks
    content = content.replace(/\{%\s*extends\s+[^%]+%\}/gi, '');
    content = content.replace(/\{%\s*block\s+(?:title|header_title|header_subtitle|company_branding|footer_signature)\s*%\}([\s\S]*?)\{%\s*endblock(?:\s+\w+)?\s*%\}/gi, '');
    content = content.replace(/\{%\s*block\s+\w+\s*%\}/gi, '');
    content = content.replace(/\{%\s*endblock(?:\s+\w+)?\s*%\}/gi, '');
  }

  // Remove any internal stray {% block ... %} and {% endblock ... %}
  content = content.replace(/\{%\s*block\s+[\w_]+\s*%\}/gi, '');
  content = content.replace(/\{%\s*endblock(?:\s+[\w_]+)?\s*%\}/gi, '');

  // Handle skills loop: convert {% for skill in selected_skills %}...{% endfor %} to styled bold placeholder
  content = content.replace(/\{%\s*for\s+skill\s+in\s+selected_skills\s*%\}(?:[\s\S]*?)\{%\s*endfor\s*%\}/gi, '<strong style="color: #2563eb; font-weight: 700;">{{ selected_skills|join:", " }}</strong>');
  content = content.replace(/<strong[^>]*>\s*\{\{\s*skill\s*\}\}\s*<\/strong>\s*,?\s*/gi, '<strong style="color: #2563eb; font-weight: 700;">{{ selected_skills|join:", " }}</strong>');
  content = content.replace(/\{\{\s*skill\s*\}\}\s*,?\s*/gi, '<strong style="color: #2563eb; font-weight: 700;">{{ selected_skills|join:", " }}</strong>');

  // Strip conditionals {% if ... %}, {% endif %}, {% elif ... %}, {% else %} while preserving the inner content
  content = content.replace(/\{%\s*if\s+[\w_.]+\s*%\}/gi, '');
  content = content.replace(/\{%\s*elif\s+[\w_.]+\s*%\}/gi, '');
  content = content.replace(/\{%\s*else\s*%\}/gi, '');
  content = content.replace(/\{%\s*endif\s*%\}/gi, '');

  // Strip any remaining stray {% ... %}
  content = content.replace(/\{%[\s\S]*?%\}/gi, '');

  // Remove stray branding table from inside body if present
  content = content.replace(/<table[^>]*>[\s\S]*?Sent on behalf of[\s\S]*?<\/table>/gi, '');
  content = content.replace(/<div[^>]*>[\s\S]*?Sent on behalf of[\s\S]*?<\/div>/gi, '');
  content = content.replace(/<p[^>]*>[\s\S]*?Sent on behalf of[\s\S]*?<\/p>/gi, '');

  // Remove empty paragraphs
  content = content.replace(/<p\s*[^>]*>\s*(?:&nbsp;)?\s*<\/p>/gi, '');

  return content.trim();
};

const TEMPLATE_CONFIGS = {
  interview_invite: {
    title: 'Interview Invitation | AptiView',
    headerTitle: '🎯 Interview Invitation',
    headerSubtitle: 'You have been invited to participate in an assessment',
    branding: true,
  },
  candidate_rejection: {
    title: 'Application Update - {{ company_name }} | AptiView',
    headerTitle: 'Update on your Application',
    headerSubtitle: 'Thank you for your interest in {{ company_name }}',
    branding: true,
  },
  candidate_selection: {
    title: 'Congratulations - Application Update | AptiView',
    headerTitle: '🎉 Congratulations!',
    headerSubtitle: '{{ company_name }}',
    branding: true,
  },
  manager_notification: {
    title: 'Congratulations - Next Round Interview | AptiView',
    headerTitle: '🎉 Congratulations - Next Round!',
    headerSubtitle: '{{ company_name }}',
    branding: true,
  },
  feedback_report: {
    title: 'Interview Feedback Report | AptiView',
    headerTitle: '📊 Interview Feedback Report',
    headerSubtitle: 'Your interview insights are ready',
    branding: false,
  },
};

export const toFullTemplate = (content, templateType = 'interview_invite') => {
  if (!content || typeof content !== 'string') return '';
  let cleaned = content;
  if (templateType === 'interview_invite') {
    if (!cleaned.includes('{% for skill in selected_skills')) {
      const loopTpl = '{% for skill in selected_skills %}<strong style="color: {{ email_color|default:\'#2563eb\' }}; font-weight: 700;">{{ skill }}</strong>{% if not forloop.last %}, {% endif %}{% empty %}<strong style="color: {{ email_color|default:\'#2563eb\' }}; font-weight: 700;">{{ selected_skills|join:", " }}</strong>{% endfor %}';
      cleaned = cleaned.replace(/<strong[^>]*>\s*\{\{\s*selected_skills(?:\|join:[^}]+)?\s*\}\}\s*<\/strong>/gi, loopTpl);
      cleaned = cleaned.replace(/\{\{\s*selected_skills(?:\|join:[^}]+)?\s*\}\}/gi, loopTpl);
    }
    if (cleaned.includes('Interview Focus Areas') && !cleaned.includes('selected_skills')) {
      cleaned = cleaned.replace(/(<div[^>]*>(?:(?!<\/div>)[\s\S])*?Interview Focus Areas[\s\S]*?<\/div>)/i, '{% if selected_skills %}\n$1\n{% endif %}');
    }
  }

  const cfg = TEMPLATE_CONFIGS[templateType] || TEMPLATE_CONFIGS.interview_invite;

  if (cleaned.includes('{% extends')) {
    if (templateType !== 'interview_invite' && cleaned.includes('🎯 Interview Invitation')) {
      cleaned = cleaned.replace(/\{%\s*block\s+header_title\s*%\}([\s\S]*?)\{%\s*endblock(?:\s+\w+)?\s*%\}/gi, `{% block header_title %}${cfg.headerTitle}{% endblock %}`);
      cleaned = cleaned.replace(/\{%\s*block\s+header_subtitle\s*%\}([\s\S]*?)\{%\s*endblock(?:\s+\w+)?\s*%\}/gi, `{% block header_subtitle %}${cfg.headerSubtitle}{% endblock %}`);
      cleaned = cleaned.replace(/\{%\s*block\s+title\s*%\}([\s\S]*?)\{%\s*endblock(?:\s+\w+)?\s*%\}/gi, `{% block title %}${cfg.title}{% endblock %}`);
    }
    return cleaned;
  }

  const brandingBlock = cfg.branding ? `{% block company_branding %}
<table width="100%" border="0" cellpadding="0" cellspacing="0" bgcolor="#eff6ff" style="background-color: #eff6ff; border-bottom: 1px solid #dbeafe; width: 100%;">
  <tr>
    <td align="center" style="padding: 10px 24px; text-align: center; font-size: 13px; color: #1e40af; font-weight: 500; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
      Sent on behalf of <strong style="color: #1e3a8a;">{{ company_name }}</strong>
    </td>
  </tr>
</table>
{% endblock %}` : '';

  return `{% extends "emails/base_email.html" %}

{% block title %}${cfg.title}{% endblock %}

{% block header_title %}${cfg.headerTitle}{% endblock %}
{% block header_subtitle %}${cfg.headerSubtitle}{% endblock %}

${brandingBlock}

{% block content %}
${cleaned.trim()}
{% endblock %}

{% block footer_signature %}
Best regards,<br/>The {{ company_name }} Hiring Team
{% endblock %}`;
};


const TEMPLATE_TYPES = [
  { id: 'interview_invite', label: 'Interview Invitation', description: 'Sent to candidates when they are invited for an interview.', icon: <EmailIcon sx={{ fontSize: 20 }} /> },
  { id: 'candidate_rejection', label: 'Candidate Rejection', description: 'Sent to candidates when they are rejected.', icon: <BlockIcon sx={{ fontSize: 20 }} /> },
  { id: 'candidate_selection', label: 'Candidate Selection', description: 'Sent to candidates when they are selected for the next round.', icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} /> },
  { id: 'manager_notification', label: 'Manager Notification', description: 'Sent to hiring managers about candidate progress.', icon: <SettingsOverscanIcon sx={{ fontSize: 20 }} /> },
  { id: 'feedback_report', label: 'Feedback Report', description: 'Sent to candidates with their interview performance feedback.', icon: <FactCheckOutlinedIcon sx={{ fontSize: 20 }} /> },
];

const EMAIL_STYLES = [
  { id: 'minimal', label: 'Minimalist Clean', icon: '✨', description: 'Simple logo & clean content', color: '#10b981', bg: '#ecfdf5' },
  { id: 'modern_card', label: 'Modern Professional', icon: '💳', description: 'Gradient header & card layout', color: '#6366f1', bg: '#eef2ff' },
  { id: 'formal', label: 'Executive Formal', icon: '🏛️', description: 'Corporate text & structured sections', color: '#0f172a', bg: '#f1f5f9' },
  { id: 'creative', label: 'Enterprise Structured', icon: '🎨', description: 'Info cards & strong CTA', color: '#f59e0b', bg: '#fffbeb' },
  { id: 'compact', label: 'Compact Direct', icon: '💬', description: 'Plain text style & inline link', color: '#ec4899', bg: '#fdf2f8' },
];

const AVAILABLE_VARIABLES = [
  {
    category: 'Candidate',
    icon: <PersonIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
    variables: ['{{ candidate_name }}', '{{ candidate_email }}'],
    appliesTo: ['interview_invite', 'candidate_rejection', 'candidate_selection', 'manager_notification', 'feedback_report'],
  },
  {
    category: 'Job',
    icon: <WorkIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
    variables: ['{{ job_title }}', '{{ company_name }}', '{{ selected_skills|join:", " }}'],
    appliesTo: ['interview_invite', 'candidate_rejection', 'candidate_selection', 'manager_notification', 'feedback_report'],
  },
  {
    category: 'Interview',
    icon: <ScheduleIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
    variables: ['{{ interview_date }}', '{{ scheduled_date }}', '{{ duration }}', '{{ interview_url }}', '{{ expiration_date }}'],
    appliesTo: ['interview_invite', 'manager_notification', 'feedback_report'],
  },
  {
    category: 'Score',
    icon: <StarIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
    variables: ['{{ company_admin_name }}', '{{ score_percentage }}'],
    appliesTo: ['manager_notification'],
  },
  {
    category: 'Rejection',
    icon: <InfoIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
    variables: ['{{ rejection_reason }}'],
    appliesTo: ['candidate_rejection'],
  },
];

export const getFullMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  return `${backendBase}${cleanPath}`;
};

const SafeIframePreview = ({ html, title }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
        const styledHtml = `
          <!DOCTYPE html>
          <html>
            <head>
              <meta charset="utf-8">
              <base href="${backendBase}/">
              <meta name="viewport" content="width=device-width, initial-scale=1">
              <style>
                html, body {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                  background-color: transparent;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                }
                ::-webkit-scrollbar {
                  width: 6px;
                  height: 6px;
                }
                ::-webkit-scrollbar-track {
                  background: rgba(0, 0, 0, 0.03);
                }
                ::-webkit-scrollbar-thumb {
                  background: #cbd5e1;
                  border-radius: 4px;
                }
                ::-webkit-scrollbar-thumb:hover {
                  background: #94a3b8;
                }
                body {
                  padding-bottom: 36px !important;
                }
              </style>
            </head>
            <body>
              ${html || '<p style="color: #94a3b8; text-align:center; padding: 60px 20px;">No content available</p>'}
            </body>
          </html>
        `;
        doc.write(styledHtml);
        doc.close();
      }
    }
  }, [html]);

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    }}>
      <iframe
        ref={iframeRef}
        title={title}
        style={{
          width: '100%',
          height: '100%',
          flex: 1,
          border: 'none',
          background: 'transparent',
          display: 'block'
        }}
      />
    </Box>
  );
};

const getSnippet = (html) => {
  if (!html) return '';
  // Remove style and script tags completely before stripping other tags
  let text = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ');
  text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ');
  // Strip remaining HTML tags and extra spaces
  text = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > 140 ? text.substring(0, 140) + '...' : text;
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EmailTemplates = ({ embedded = false }) => {
  const { t } = useTranslation();
  const { userDetails } = useUser();
  const MAX_TEMPLATES = parseInt(process.env.REACT_APP_EMAIL_TEMPLATE_MAX_COUNT || '5', 10) || 5;
  const [selectedType, setSelectedType] = useState(TEMPLATE_TYPES[0]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'editor'
  const [loading, setLoading] = useState(false);
  usePageLoading(loading && viewMode === 'list', {
    message: t('emailTemplates.loading', 'Loading templates…'),
  });
  const [saving, setSaving] = useState(false);

  // Send Test Email state
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailCc, setTestEmailCc] = useState('');
  const [testEmailSubject, setTestEmailSubject] = useState('');
  const [testEmailIncludeChanges, setTestEmailIncludeChanges] = useState(true);
  const [testEmailUseSampleData, setTestEmailUseSampleData] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);

  // Storage for our fetched templates
  const [templates, setTemplates] = useState([]);
  const [defaultTemplate, setDefaultTemplate] = useState({ subject: '', html_content: '' });

  const used = Array.isArray(templates) ? templates.filter(t => t.template_type === selectedType.id).length : 0;
  const isLimitReached = used >= MAX_TEMPLATES;

  // Editor state
  const [editingTemplate, setEditingTemplate] = useState({ name: '', subject: '', html_content: '', is_active: false, visual_style: 'minimal' });
  const [nameError, setNameError] = useState(false);
  const nameInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = Preview, 1 = Edit HTML
  const [previewHtml, setPreviewHtml] = useState('');
  const [editorMode, setEditorMode] = useState('rich'); // 'rich' or 'code'

  const handleSwitchToRich = () => {
    setEditingTemplate(prev => ({
      ...prev,
      html_content: extractVisualContent(prev.html_content)
    }));
    setEditorMode('rich');
  };
  const [displayStyle, setDisplayStyle] = useState('grid'); // 'grid' or 'list' for the main library view
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewTab, setViewTab] = useState(0); // 0: Active, 1: Archived

  // Custom colors and style states for Editor
  const [selectedStyleId, setSelectedStyleId] = useState('minimal');
  const [styleMenuAnchor, setStyleMenuAnchor] = useState(null);
  const [varMenuAnchor, setVarMenuAnchor] = useState(null);
  const [copiedVar, setCopiedVar] = useState(null);
  const [headerColor, setHeaderColor] = useState('#2563eb');
  const [footerColor, setFooterColor] = useState('#f8fafc');
  const [emailColor, setEmailColor] = useState('#2563eb');

  // Fullscreen Preview states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewStyleId, setPreviewStyleId] = useState('minimal');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [previewClient, setPreviewClient] = useState('gmail');
  const [previewHeaderColor, setPreviewHeaderColor] = useState('#2563eb');
  const [previewFooterColor, setPreviewFooterColor] = useState('#f8fafc');
  const [previewEmailColor, setPreviewEmailColor] = useState('#2563eb');
  const [previewBrandingMode, setPreviewBrandingMode] = useState('co_branded');
  const [previewHeaderMode, setPreviewHeaderMode] = useState('logo_and_text');
  const [previewLogoUrl, setPreviewLogoUrl] = useState('');

  // Live Variables state
  const [liveVariables, setLiveVariables] = useState({
    candidate_name: 'John Doe',
    company_name: 'Omnirises',
    job_title: 'frontend developer',
    interview_date: '24 October 2026',
    scheduled_date: 'October 24, 2026',
    duration: '30 minutes',
    expiration_date: 'November 11, 2026 at 5:55 AM',
    selected_skills: ['React.js', 'JavaScript', 'TypeScript', 'REST APIs'],
    skill: 'React.js, JavaScript, TypeScript, REST APIs',
    interview_time: '10:00 AM (IST)',
    interview_link: 'https://aptiview.ai/interview/123',
    interview_url: 'https://aptiview.ai/interview/123',
    company_admin_name: 'Aman Dwivedi',
    score_percentage: '85.50%',
    recruiter_name: '',
    support_email: 'support@aptiview.ai',
    contact_email: 'interview@omnirises.com',
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: () => { },
    confirmButtonText: ''
  });

  const fetchTemplatesByType = useCallback(async (typeId, includeArchived = false) => {
    try {
      setLoading(true);
      const url = includeArchived
        ? `/email-templates/by_type/?template_type=${typeId}&include_archived=true`
        : `/email-templates/by_type/?template_type=${typeId}`;
      const res = await api.get(url);
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error fetching templates by type:', e);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDefault = useCallback(async (typeId) => {
    try {
      const response = await api.get(`/email-templates/get_default/?template_type=${typeId}`);
      const data = response.data;
      const tpl = {
        name: 'System Default',
        template_type: typeId,
        subject: data.subject || '',
        html_content: data.html_content || '',
        visual_style: data.visual_style || 'minimal',
        isSystemDefault: true,
      };
      setDefaultTemplate(tpl);
      return tpl;
    } catch (error) {
      console.error('Error fetching default template:', error);
      return null;
    }
  }, []);

  const renderPreviewHtml = async (htmlContent, styleId, colors = null, customVars = null, typeId = null) => {
    try {
      const activeTypeId = typeId || selectedType?.id || 'interview_invite';
      const fullHtml = toFullTemplate(htmlContent, activeTypeId);
      const parts = (styleId || 'minimal').split('|');
      const baseStyle = parts[0];
      const hColor = colors ? colors.header_color : (parts[1] || headerColor);
      const fColor = colors ? colors.footer_color : (parts[2] || footerColor);
      const eColor = colors ? colors.email_color : (parts[3] || emailColor);
      const bMode = parts[4] || previewBrandingMode;
      const hMode = parts[5] || previewHeaderMode;
      const logoUrl = parts.slice(6).join('|') || previewLogoUrl;

      const fullStyleStr = `${baseStyle}|${hColor}|${fColor}|${eColor}|${bMode}|${hMode}|${logoUrl}`;

      const previewResp = await api.post('/email-templates/preview/', {
        html_content: fullHtml || '',
        visual_style: fullStyleStr,
        template_type: activeTypeId,
        dummy_context: {
          ...(customVars || liveVariables),
          email_color: eColor,
          header_color: hColor,
          footer_color: fColor,
          branding_mode: bMode,
          header_mode: hMode,
          logo_url: logoUrl
        }
      });
      const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
      let rendered = previewResp.data.rendered_html || '';
      if (rendered) {
        rendered = rendered.replace(/src=["']\/media\//g, `src="${backendBase}/media/`);
        rendered = rendered.replace(/src=["']media\//g, `src="${backendBase}/media/`);
      }
      setPreviewHtml(rendered);
    } catch (e) {
      console.warn('Custom preview rendering failed', e);
      setPreviewHtml(htmlContent || '');
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchTemplatesByType(selectedType.id, true);
      await fetchDefault(selectedType.id);
    };
    init();
  }, [fetchTemplatesByType, fetchDefault, selectedType.id, viewTab]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setViewMode('list');
    setViewTab(0); // Reset to active tab when changing type
    fetchTemplatesByType(type.id, true);
    fetchDefault(type.id);
  };

  const handleEditDefaultTemplate = async () => {
    setNameError(false);
    let curDefault = defaultTemplate;
    if (!curDefault || !curDefault.html_content) {
      curDefault = await fetchDefault(selectedType.id);
    }
    if (!curDefault) {
      toast.error(t('emailTemplates.toast.defaultLoadFailed', 'Failed to load default template'));
      return;
    }
    await openEditor({
      name: '',
      subject: curDefault.subject || '',
      html_content: curDefault.html_content || '',
      visual_style: curDefault.visual_style || 'minimal',
      isSystemDefault: false,
      id: null
    }, true);
  };

  const openEditor = async (templateObj, startInEdit = false) => {
    setNameError(false);
    setLoading(true);

    // Parse visual style colors
    const visualStyleStr = templateObj.visual_style || 'minimal';
    const parts = visualStyleStr.split('|');
    const baseStyle = parts[0] || 'minimal';
    const hColor = parts[1] || '#2563eb';
    const fColor = parts[2] || '#f8fafc';
    const eColor = parts[3] || '#2563eb';
    const bMode = parts[4] || 'co_branded';
    const hMode = parts[5] || 'logo_and_text';
    const logoUrl = parts.slice(6).join('|') || '';

    const cleanBody = extractVisualContent(templateObj.html_content);

    setEditingTemplate({
      ...templateObj,
      html_content: cleanBody,
      template_type: selectedType.id,
      visual_style: visualStyleStr
    });

    setSelectedStyleId(baseStyle);
    setHeaderColor(hColor);
    setFooterColor(fColor);
    setEmailColor(eColor);
    setPreviewBrandingMode(bMode);
    setPreviewHeaderMode(hMode);
    setPreviewLogoUrl(logoUrl);

    setEditorMode('rich');
    setActiveTab(startInEdit ? 1 : 0);
    setViewMode('editor');

    if (templateObj.html_content) {
      await renderPreviewHtml(
        cleanBody,
        visualStyleStr,
        { header_color: hColor, footer_color: fColor, email_color: eColor },
        liveVariables
      );
    } else {
      setPreviewHtml('');
    }
    setLoading(false);
  };

  const handleSave = async (makeActive = false) => {
    const templateToSave = previewDialogOpen ? previewTemplate : editingTemplate;
    if (!templateToSave) {
      toast.warning('No template data found to save');
      return false;
    }

    const nameToSave = (templateToSave.name || editingTemplate.name || (previewDialogOpen ? `${selectedType.label} (Custom)` : '')).trim();
    if (!nameToSave) {
      setNameError(true);
      toast.warning(t('emailTemplates.validation.nameRequired', 'Please enter a template name before saving'));
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
      return false;
    }
    setNameError(false);

    const subjectToSave = templateToSave.subject || editingTemplate.subject || `${selectedType.label}`;
    if (!subjectToSave || !subjectToSave.trim()) {
      toast.warning(t('emailTemplates.validation.subjectRequired', 'Please enter a subject line'));
      return false;
    }

    setSaving(true);
    try {
      let styleId = selectedStyleId;
      let hColor = headerColor;
      let fColor = footerColor;
      let eColor = emailColor;
      let bMode = previewBrandingMode;
      let hMode = previewHeaderMode;
      let logoUrl = previewLogoUrl;

      if (previewDialogOpen) {
        styleId = previewStyleId;
        hColor = previewHeaderColor;
        fColor = previewFooterColor;
        eColor = previewEmailColor;
        bMode = previewBrandingMode;
        hMode = previewHeaderMode;
        logoUrl = previewLogoUrl;
      } else if (!logoUrl && templateToSave?.visual_style) {
        const vParts = templateToSave.visual_style.split('|');
        if (vParts.length > 6 && vParts.slice(6).join('|')) {
          logoUrl = vParts.slice(6).join('|');
        }
      }

      // Sanitize payload to exclude UI-only helper fields like isSystemDefault
      const { isSystemDefault, ...sanitizedTemplate } = templateToSave;

      const fullHtml = toFullTemplate(templateToSave.html_content, selectedType.id);
      const payload = {
        name: nameToSave,
        ...sanitizedTemplate,
        html_content: fullHtml,
        subject: subjectToSave,
        template_type: selectedType.id,
        visual_style: `${styleId}|${hColor}|${fColor}|${eColor}|${bMode}|${hMode}|${logoUrl}`,
        ...(makeActive ? { is_active: true } : {})
      };

      let savedId = templateToSave.id;
      if (templateToSave.id) {
        await api.put(`/email-templates/${templateToSave.id}/`, payload);
        toast.success(makeActive ? 'Template saved & set as active!' : t('emailTemplates.toast.updated', 'Template updated successfully'));
      } else {
        const res = await api.post('/email-templates/', payload);
        savedId = res.data?.id;
        toast.success(makeActive ? 'Template created & set as active!' : t('emailTemplates.toast.created', 'New template created successfully'));
      }

      if (makeActive && savedId) {
        await api.post(`/email-templates/${savedId}/set_active/`);
      }

      await fetchTemplatesByType(selectedType.id, true);
      setViewMode('list');
      return true;
    } catch (error) {
      console.error('Error saving template:', error);
      let errorMsg = t('emailTemplates.toast.saveFailed', 'Failed to save template');
      if (error?.response?.data) {
        if (typeof error.response.data === 'object') {
          errorMsg = Object.entries(error.response.data)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join(' | ');
        } else if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        }
      }
      toast.error(errorMsg);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (id) => {
    try {
      setLoading(true);
      await api.post(`/email-templates/${id}/set_active/`);
      toast.success(t('emailTemplates.toast.setActive', 'Template set as active'));
      await fetchTemplatesByType(selectedType.id, true);
    } catch (e) {
      console.error('Error setting active template:', e);
      toast.error(t('emailTemplates.toast.setActiveFailed', 'Failed to set active template'));
    } finally {
      setLoading(false);
    }
  };

  const setSystemDefaultActive = async () => {
    try {
      setLoading(true);
      await api.post('/email-templates/deactivate_all/', {
        template_type: selectedType.id
      });
      toast.success(t('emailTemplates.toast.systemDefaultActivated', 'System Default set as active'));
      await fetchTemplatesByType(selectedType.id, true);
    } catch (e) {
      console.error('Error setting default active:', e);
      toast.error(t('emailTemplates.toast.systemDefaultActivateFailed', 'Failed to activate default template'));
    } finally {
      setLoading(false);
    }
  };

  const archiveTemplate = async (id) => {
    setConfirmDialog({
      open: true,
      title: t('emailTemplates.confirm.archiveTitle', 'Archive Template'),
      content: t('emailTemplates.confirm.archive', 'This template will still count toward your {{count}}-template limit. Are you sure you want to archive it?', { count: MAX_TEMPLATES }),
      confirmButtonText: 'Archive',
      onConfirm: async () => {
        try {
          setLoading(true);
          await api.patch(`/email-templates/${id}/`, { is_archived: true, is_active: false });
          toast.success(t('emailTemplates.toast.archived', 'Template archived successfully'));
          await fetchTemplatesByType(selectedType.id, true);
        } catch (e) {
          console.error('Error archiving template:', e);
          toast.error(t('emailTemplates.toast.archiveFailed', 'Failed to archive template'));
        } finally {
          setLoading(false);
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      }
    });
  };

  const restoreTemplate = async (id) => {
    try {
      setLoading(true);
      // Use dedicated endpoint so backend can enforce limits and return clear errors
      await api.post(`/email-templates/${id}/unarchive/`);
      toast.success(t('emailTemplates.toast.restored', 'Template restored to active list'));
      await fetchTemplatesByType(selectedType.id, true);
    } catch (e) {
      console.error('Error restoring template:', e);
      const msg = e?.response?.data?.detail || e?.response?.data || 'Failed to restore template';
      toast.error(msg || t('emailTemplates.toast.restoreFailed', 'Failed to restore template'));
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id) => {
    setConfirmDialog({
      open: true,
      title: t('emailTemplates.confirm.deleteTitle', 'Delete Template'),
      content: t('emailTemplates.confirm.delete', 'Are you sure you want to permanently delete this template? This action cannot be undone.'),
      confirmButtonText: 'Delete',
      onConfirm: async () => {
        try {
          setLoading(true);
          await api.delete(`/email-templates/${id}/`);
          toast.success(t('emailTemplates.toast.deleted', 'Template deleted successfully'));
          await fetchTemplatesByType(selectedType.id, true);
        } catch (e) {
          console.error('Error deleting template:', e);
          toast.error(t('emailTemplates.toast.deleteFailed', 'Failed to delete template'));
        } finally {
          setLoading(false);
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      }
    });
  };

  const handleReset = () => {
    setConfirmDialog({
      open: true,
      title: t('emailTemplates.confirm.resetTitle', 'Reset Template'),
      content: t('emailTemplates.confirm.reset', 'Reset to system default? Your customized template will be reverted.'),
      confirmButtonText: 'Reset',
      onConfirm: () => {
        setEditingTemplate({
          id: editingTemplate.id,
          template_type: selectedType.id,
          subject: defaultTemplate.subject,
          html_content: defaultTemplate.html_content,
          visual_style: 'minimal',
          is_active: true,
        });
        renderPreviewHtml(defaultTemplate.html_content, 'minimal');
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    });
  };

  const handlePreviewRefresh = async () => {
    await renderPreviewHtml(editingTemplate.html_content, editingTemplate.visual_style);
    setActiveTab(0);
  };

  const handleStyleChange = async (styleId) => {
    setSelectedStyleId(styleId);
    const newStyleStr = `${styleId}|${headerColor}|${footerColor}|${emailColor}|${previewBrandingMode}|${previewHeaderMode}|${previewLogoUrl}`;
    setEditingTemplate((prev) => ({
      ...prev,
      visual_style: newStyleStr
    }));
    await renderPreviewHtml(editingTemplate.html_content, newStyleStr, { header_color: headerColor, footer_color: footerColor, email_color: emailColor }, liveVariables, selectedType?.id);
    setActiveTab(0); // Switch to Preview tab to immediately display new style
    const selectedStyleObj = EMAIL_STYLES.find(s => s.id === styleId);
    toast.success(`Visual style applied: ${selectedStyleObj?.label || styleId}`, { autoClose: 1800 });
  };

  const handleColorChange = async (type, val) => {
    let newH = headerColor;
    let newF = footerColor;
    let newE = emailColor;
    if (type === 'header') { newH = val; setHeaderColor(val); }
    if (type === 'footer') { newF = val; setFooterColor(val); }
    if (type === 'email') { newE = val; setEmailColor(val); }
    const newStyleStr = `${selectedStyleId}|${newH}|${newF}|${newE}|${previewBrandingMode}|${previewHeaderMode}|${previewLogoUrl}`;
    await renderPreviewHtml(editingTemplate.html_content, newStyleStr, { header_color: newH, footer_color: newF, email_color: newE }, liveVariables, selectedType?.id);
  };

  const handleOpenPreviewDialog = async (templateObj) => {
    const visualStyleStr = templateObj.visual_style || 'minimal';
    const parts = visualStyleStr.split('|');
    const baseStyle = parts[0] || 'minimal';
    const hColor = parts[1] || '#2563eb';
    const fColor = parts[2] || '#f8fafc';
    const eColor = parts[3] || '#2563eb';
    const bMode = parts[4] || 'co_branded';
    const hMode = parts[5] || 'logo_and_text';
    const logoUrl = parts.slice(6).join('|') || '';

    const activeType = templateObj.template_type || selectedType?.id || 'interview_invite';
    setPreviewTemplate({ ...templateObj, template_type: activeType });
    setPreviewStyleId(baseStyle);
    setPreviewHeaderColor(hColor);
    setPreviewFooterColor(fColor);
    setPreviewEmailColor(eColor);
    setPreviewBrandingMode(bMode);
    setPreviewHeaderMode(hMode);
    setPreviewLogoUrl(logoUrl);
    setPreviewDevice('desktop');
    setPreviewDialogOpen(true);

    await renderPreviewHtmlForDialog(templateObj.html_content, baseStyle, hColor, fColor, eColor, bMode, hMode, logoUrl, liveVariables, activeType);
  };

  const renderPreviewHtmlForDialog = async (htmlContent, styleId, hColor, fColor, eColor, bMode, hMode, logoUrl, vars, typeId = null) => {
    try {
      const activeTypeId = typeId || previewTemplate?.template_type || selectedType?.id || 'interview_invite';
      const fullHtml = toFullTemplate(htmlContent, activeTypeId);
      const fullStyleStr = `${styleId}|${hColor}|${fColor}|${eColor}|${bMode}|${hMode}|${logoUrl}`;
      const previewResp = await api.post('/email-templates/preview/', {
        html_content: fullHtml || '',
        visual_style: fullStyleStr,
        template_type: activeTypeId,
        dummy_context: {
          ...vars,
          email_color: eColor,
          header_color: hColor,
          footer_color: fColor,
          branding_mode: bMode,
          header_mode: hMode,
          logo_url: logoUrl
        }
      });
      const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
      let rendered = previewResp.data.rendered_html || '';
      if (rendered) {
        rendered = rendered.replace(/src=["']\/media\//g, `src="${backendBase}/media/`);
        rendered = rendered.replace(/src=["']media\//g, `src="${backendBase}/media/`);
      }
      setPreviewHtml(rendered);
    } catch (e) {
      console.warn('Dialog preview rendering failed', e);
      setPreviewHtml(htmlContent || '');
    }
  };

  const handleDialogStyleChange = async (styleId) => {
    setPreviewStyleId(styleId);
    await renderPreviewHtmlForDialog(previewTemplate.html_content, styleId, previewHeaderColor, previewFooterColor, previewEmailColor, previewBrandingMode, previewHeaderMode, previewLogoUrl, liveVariables);
  };

  const handleDialogColorChange = async (type, val) => {
    let newH = previewHeaderColor;
    let newF = previewFooterColor;
    let newE = previewEmailColor;
    if (type === 'header') { newH = val; setPreviewHeaderColor(val); }
    if (type === 'footer') { newF = val; setPreviewFooterColor(val); }
    if (type === 'email') { newE = val; setPreviewEmailColor(val); }
    await renderPreviewHtmlForDialog(previewTemplate.html_content, previewStyleId, newH, newF, newE, previewBrandingMode, previewHeaderMode, previewLogoUrl, liveVariables);
  };

  const handleDialogVariableChange = async (name, val) => {
    const updatedVars = { ...liveVariables, [name]: val };
    setLiveVariables(updatedVars);
    await renderPreviewHtmlForDialog(previewTemplate.html_content, previewStyleId, previewHeaderColor, previewFooterColor, previewEmailColor, previewBrandingMode, previewHeaderMode, previewLogoUrl, updatedVars);
  };

  const handleDialogBrandingModeChange = async (val) => {
    setPreviewBrandingMode(val);
    await renderPreviewHtmlForDialog(previewTemplate.html_content, previewStyleId, previewHeaderColor, previewFooterColor, previewEmailColor, val, previewHeaderMode, previewLogoUrl, liveVariables);
  };

  const handleDialogHeaderModeChange = async (val) => {
    setPreviewHeaderMode(val);
    await renderPreviewHtmlForDialog(previewTemplate.html_content, previewStyleId, previewHeaderColor, previewFooterColor, previewEmailColor, previewBrandingMode, val, previewLogoUrl, liveVariables);
  };

  const handleDialogLogoChange = async (val) => {
    setPreviewLogoUrl(val);
    await renderPreviewHtmlForDialog(previewTemplate.html_content, previewStyleId, previewHeaderColor, previewFooterColor, previewEmailColor, previewBrandingMode, previewHeaderMode, val, liveVariables);
  };

  const handleSendTestEmail = () => {
    setTestEmailSubject(`${previewTemplate?.subject || editingTemplate?.subject || 'Interview Invitation'} (Preview)`);
    setTestEmailTo(userDetails?.email || '');
    setTestEmailCc('');
    setTestEmailIncludeChanges(true);
    setTestEmailUseSampleData(true);
    setTestEmailOpen(true);
  };

  const executeSendTestEmail = async () => {
    if (!testEmailTo.trim()) {
      toast.warning('Please enter at least one recipient email address.');
      return;
    }

    setSendingTest(true);
    try {
      let htmlToSend = previewTemplate?.html_content || '';
      let styleToSend = `${previewStyleId}|${previewHeaderColor}|${previewFooterColor}|${previewEmailColor}|${previewBrandingMode}|${previewHeaderMode}|${previewLogoUrl}`;

      if (!testEmailIncludeChanges && previewTemplate?.id) {
        const savedTemplate = templates.find(t => t.id === previewTemplate.id);
        if (savedTemplate) {
          htmlToSend = savedTemplate.html_content;
          styleToSend = savedTemplate.visual_style;
        }
      }

      const activeTypeId = previewTemplate?.template_type || selectedType?.id || 'interview_invite';
      const fullHtmlToSend = toFullTemplate(htmlToSend, activeTypeId);

      const logoToSend = previewLogoUrl || styleToSend.split('|').slice(6).join('|');

      await api.post('/email-templates/send_test/', {
        html_content: fullHtmlToSend,
        template_type: activeTypeId,
        visual_style: styleToSend,
        subject: testEmailSubject,
        to_emails: testEmailTo,
        cc_emails: testEmailCc,
        use_sample_data: testEmailUseSampleData,
        dummy_context: testEmailUseSampleData ? {
          ...liveVariables,
          logo_url: logoToSend,
          email_color: previewEmailColor,
          header_color: previewHeaderColor,
          footer_color: previewFooterColor,
          branding_mode: previewBrandingMode,
          header_mode: previewHeaderMode,
        } : {
          logo_url: logoToSend,
        }
      });

      toast.success('Test email sent successfully!');
      setTestEmailOpen(false);
    } catch (e) {
      console.error('Failed to send test email:', e);
      const msg = e?.response?.data?.error || e?.response?.data?.detail || 'Failed to send test email';
      toast.error(msg);
    } finally {
      setSendingTest(false);
    }
  };

  const handleCopyPreviewHtml = () => {
    navigator.clipboard.writeText(previewHtml);
    toast.success('Rendered HTML template copied to clipboard!');
  };

  const handleDownloadPreviewHtml = () => {
    const blob = new Blob([previewHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${previewTemplate?.name || 'template'}_preview.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Email template file downloaded successfully!');
  };

  const handleCopyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast.info(`Copied: ${variable}`, { autoClose: 1500 });
  };

  const handleDragStart = (e, variable) => {
    e.dataTransfer.setData('text/plain', variable);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const filteredTemplates = Array.isArray(templates) ? templates.filter(t => t.template_type === selectedType.id) : [];

  const layout = (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      gap: { xs: 2, md: 3 },
      flex: 1,
      width: '100%',
      minHeight: 0,
      height: '100%',
      overflow: 'hidden',
    }}>
      {/* LEFT SIDEBAR - Template Library - Compact & Balanced Width */}
      <Box sx={{
        width: {
          xs: '100%',
          md: sidebarCollapsed ? '54px' : '215px',
          lg: sidebarCollapsed ? '56px' : '225px'
        },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { md: '100%' },
        overflow: 'hidden',
        zIndex: 10,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.06)',
        p: 0
      }}>
        <Box sx={{
          px: sidebarCollapsed ? 0.75 : 1.75,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          borderBottom: '1px solid #f1f5f9',
          mb: 0.5
        }}>
          {!sidebarCollapsed && (
            <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', letterSpacing: '0.8px', whiteSpace: 'nowrap' }}>
              TEMPLATE LIBRARY
            </Typography>
          )}
          <Tooltip title={sidebarCollapsed ? t('emailTemplates.expandSidebar', 'Expand Sidebar') : t('emailTemplates.collapseSidebar', 'Collapse Sidebar')}>
            <IconButton
              size="small"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              sx={{
                color: '#64748b',
                display: { xs: 'none', md: 'flex' }, // Hide on mobile
                width: 32,
                height: 32,
                backgroundColor: sidebarCollapsed ? '#f8fafc' : 'transparent',
                '&:hover': { backgroundColor: '#f1f5f9', color: '#7c3aed' }
              }}
            >
              {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <List sx={{
          p: 0.75,
          display: { xs: 'flex', md: 'block' },
          flexDirection: { xs: 'row', md: 'column' },
          overflowX: { xs: 'auto', md: 'visible' },
          gap: 0.5,
          '&::-webkit-scrollbar': { height: '4px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' }
        }}>
          {TEMPLATE_TYPES.map((type) => (
            <ListItem key={type.id} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip
                title={t(`emailTemplates.types.${type.id}.label`, { defaultValue: type.label })}
                placement="right"
                arrow
                FadeProps={{ timeout: 600 }}
              >
                <ListItemButton
                  onClick={() => handleTypeSelect(type)}
                  sx={{
                    px: sidebarCollapsed ? 0 : 1.5,
                    py: 1,
                    minHeight: 40,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    borderRadius: '10px',
                    position: 'relative',
                    backgroundColor: selectedType.id === type.id ? 'rgba(124, 58, 237, 0.08)' : 'transparent',
                    border: '1px solid',
                    borderColor: selectedType.id === type.id ? 'rgba(124, 58, 237, 0.2)' : 'transparent',
                    '&:hover': {
                      backgroundColor: selectedType.id === type.id ? 'rgba(124, 58, 237, 0.12)' : '#f8fafc',
                      transform: sidebarCollapsed ? 'none' : 'translateX(3px)',
                    },
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden'
                  }}
                >
                  {/* Active Indicator Bar */}
                  {selectedType.id === type.id && (
                    <Box sx={{
                      position: 'absolute',
                      left: 0,
                      top: '20%',
                      bottom: '20%',
                      width: '3.5px',
                      background: 'linear-gradient(to bottom, #8b5cf6, #d946ef)',
                      borderRadius: '0 4px 4px 0'
                    }} />
                  )}

                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    gap: sidebarCollapsed ? 0 : 1.5,
                    width: '100%',
                    zIndex: 1
                  }}>
                    <Box sx={{
                      color: selectedType.id === type.id ? '#7c3aed' : '#94a3b8',
                      display: 'flex',
                      width: 24,
                      height: 24,
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'color 0.3s',
                      '& svg': { fontSize: 20 }
                    }}>
                      {type.icon}
                    </Box>
                    {!sidebarCollapsed && (
                      <Typography sx={{
                        fontWeight: selectedType.id === type.id ? 700 : 600,
                        fontSize: '0.8125rem',
                        color: selectedType.id === type.id ? '#1e293b' : '#64748b',
                        transition: 'color 0.3s',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {t(`emailTemplates.types.${type.id}.label`, { defaultValue: type.label })}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* MAIN CONTENT AREA - Fluid Width */}
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {viewMode === 'list' ? (
          <Fade in style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Paper elevation={0} sx={{
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              background: 'white',
              p: { xs: 2.5, md: 3 },
              flex: 1,
              minHeight: 0,
              overflowY: 'auto !important',
              display: 'flex',
              flexDirection: 'column',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: '#f8fafc', borderRadius: '8px' },
              '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '8px', '&:hover': { background: '#94a3b8' } }
            }}>
              {/* List View Header */}
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', letterSpacing: '0.8px' }}>
                  {t('emailTemplates.breadcrumb.main', 'TEMPLATES')} &nbsp;&gt;&nbsp; <span style={{ color: '#9333ea', fontWeight: 700 }}>{t('emailTemplates.breadcrumb.highlight', 'RECRUITMENT')}</span>
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mt: 1, gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                      {selectedType.label} Templates
                    </Typography>
                    <Tabs
                      value={viewTab}
                      onChange={(e, v) => setViewTab(v)}
                      sx={{
                        minHeight: '40px',
                        background: '#f8fafc',
                        p: 0.5,
                        borderRadius: '12px',
                        '& .MuiTab-root': {
                          minHeight: '32px',
                          py: 1,
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: '10px',
                          minWidth: 'auto',
                          px: 3,
                          color: '#64748b',
                          '&.Mui-selected': {
                            color: '#7c3aed',
                            background: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          },
                          transition: 'all 0.2s'
                        },
                        '& .MuiTabs-indicator': {
                          display: 'none'
                        }
                      }}
                    >
                      <Tab label={`All (${templates.filter(t => !t.is_archived).length + (viewTab === 0 ? 1 : 0)})`} />
                      <Tab label="Archived" />
                    </Tabs>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' }, justifyContent: 'space-between' }}>
                    {/* Grid/List Toggle */}
                    <Box sx={{ display: 'flex', gap: 0.5, background: '#f1f5f9', p: 0.5, borderRadius: '10px' }}>
                      <IconButton
                        size="small"
                        onClick={() => setDisplayStyle('grid')}
                        sx={{
                          borderRadius: '8px',
                          background: displayStyle === 'grid' ? 'white' : 'transparent',
                          boxShadow: displayStyle === 'grid' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                          transition: 'all 0.2s',
                          '&:hover': { background: displayStyle === 'grid' ? 'white' : 'rgba(0,0,0,0.05)' }
                        }}
                      >
                        <GridViewIcon sx={{ fontSize: 18, color: displayStyle === 'grid' ? '#6366f1' : '#64748b' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDisplayStyle('list')}
                        sx={{
                          borderRadius: '8px',
                          background: displayStyle === 'list' ? 'white' : 'transparent',
                          boxShadow: displayStyle === 'list' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                          transition: 'all 0.2s',
                          '&:hover': { background: displayStyle === 'list' ? 'white' : 'rgba(0,0,0,0.05)' }
                        }}
                      >
                        <ViewListIcon sx={{ fontSize: 18, color: displayStyle === 'list' ? '#6366f1' : '#64748b' }} />
                      </IconButton>
                    </Box>

                    <Tooltip title={isLimitReached ? t('emailTemplates.limitReached', { count: MAX_TEMPLATES, defaultValue: `You’ve reached your limit of ${MAX_TEMPLATES} templates (including archived). Please permanently delete one to create a new template.` }) : ""}>
                      <span>
                        <Button
                          variant="contained"
                          disabled={isLimitReached}
                          onClick={() => {
                            const localizedTypeLabel = t(`emailTemplates.types.${selectedType.id}.label`, { defaultValue: selectedType.label });
                            openEditor({
                              name: t('emailTemplates.newName', { typeLabel: localizedTypeLabel, defaultValue: `New ${localizedTypeLabel}` }),
                              subject: t('emailTemplates.newSubject', { typeLabel: localizedTypeLabel, defaultValue: `${localizedTypeLabel}` }),
                              html_content: `<p>Hello {{ candidate_name }},</p><p>Enter your email content here...</p>`,
                              visual_style: 'minimal'
                            }, true);
                          }}
                          sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 700,
                            background: isLimitReached ? '#e2e8f0' : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                            px: { xs: 1.5, sm: 3 },
                            py: 1,
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            boxShadow: isLimitReached ? 'none' : '0 4px 14px 0 rgba(139, 92, 246, 0.39)',
                            '&:hover': {
                              background: isLimitReached ? '#e2e8f0' : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                              boxShadow: isLimitReached ? 'none' : '0 6px 20px rgba(139, 92, 246, 0.23)',
                              transform: isLimitReached ? 'none' : 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                          }}
                        >
                          {t('emailTemplates.createTemplate', '+ Create Template')}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}><AptiviewLoader size="sm" message={t('emailTemplates.loading', 'Loading templates…')} /></Box>
              ) : displayStyle === 'grid' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                  {/* Always show the System Default Template */}
                  <Box sx={{ width: '100%' }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        border: !templates.some(t => t.is_active) ? '2px solid #22c55e' : '1px solid #e2e8f0',
                        borderLeft: '6px solid #f59e0b',
                        backgroundColor: !templates.some(t => t.is_active) ? '#f0fdf4' : '#fffdfa',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&:hover': {
                          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                          transform: 'translateY(-4px)',
                          borderColor: !templates.some(t => t.is_active) ? '#22c55e' : '#cbd5e1'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' } }}>
                        <Box sx={{ flex: 1, width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.1rem' }}>{t('emailTemplates.systemDefault', 'System Default')}</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {!templates.some(t => t.is_active) && (
                                <Chip
                                  label={t('emailTemplates.chips.active', 'ACTIVE')}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.65rem',
                                    fontWeight: 900,
                                    backgroundColor: '#dcfce7',
                                    color: '#16a34a',
                                    borderRadius: '6px'
                                  }}
                                />
                              )}
                              <Chip
                                label={t('emailTemplates.chips.default', 'DEFAULT')}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.65rem',
                                  fontWeight: 900,
                                  backgroundColor: '#fef3c7',
                                  color: '#d97706',
                                  borderRadius: '6px'
                                }}
                                icon={<StarIcon sx={{ fontSize: '12px !important', color: '#d97706' }} />}
                              />
                            </Box>
                            <Chip
                              label={t('emailTemplates.chips.minimal', 'MINIMAL')}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                backgroundColor: '#f1f5f9',
                                color: '#475569',
                                borderRadius: '6px'
                              }}
                            />
                          </Box>
                          <Typography sx={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: 600, mb: 1 }}>
                            {t('emailTemplates.subjectLabel', 'Subject:')} {defaultTemplate.subject || t('emailTemplates.defaultSubjectFallback', 'Standard Invitation')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mb: 2, fontStyle: 'italic', lineHeight: 1.5 }}>
                            "{getSnippet(defaultTemplate.html_content)}"
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                          <Tooltip title={t('emailTemplates.preview', 'Preview')}>
                            <IconButton onClick={() => handleOpenPreviewDialog(defaultTemplate)} sx={{ color: '#64748b', border: '1.5px solid #f1f5f9', '&:hover': { backgroundColor: '#f8fafc' } }}>
                              <PreviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('emailTemplates.editDefaultTemplate', 'Edit Default Template')}>
                            <IconButton
                              onClick={handleEditDefaultTemplate}
                              sx={{
                                color: '#8b5cf6',
                                border: '1.5px solid rgba(139,92,246,0.1)',
                                '&:hover': { backgroundColor: 'rgba(139,92,246,0.05)' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {templates.some(t => t.is_active) && (
                            <Tooltip title={t('emailTemplates.setAsActive', 'Set as Active')}>
                              <IconButton onClick={setSystemDefaultActive} sx={{ color: '#10b981', border: '1.5px solid rgba(16,185,129,0.1)', '&:hover': { backgroundColor: 'rgba(16,185,129,0.05)' } }}>
                                <StarIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Box>

                  {/* Custom Templates */}
                  {templates.filter(t => !t.is_default && (viewTab === 1 ? t.is_archived : !t.is_archived)).map((tpl) => (
                    <Box sx={{ width: '100%' }} key={tpl.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: '20px',
                          border: tpl.is_active ? '2px solid #22c55e' : '1px solid #e2e8f0',
                          borderLeft: tpl.is_active ? '6px solid #22c55e' : (tpl.is_default ? '6px solid #f59e0b' : '6px solid #8b5cf6'),
                          backgroundColor: tpl.is_active ? '#f0fdf4' : (tpl.is_default ? '#fffdfa' : '#fff'),
                          opacity: tpl.is_archived ? 0.7 : 1,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                            transform: 'translateY(-4px)',
                            borderColor: tpl.is_active ? '#22c55e' : '#cbd5e1'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' } }}>
                          <Box sx={{ flex: 1, width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontWeight: 800, color: tpl.is_archived ? '#94a3b8' : '#1e293b', fontSize: '1.1rem' }}>
                                {tpl.is_default ? t('emailTemplates.systemDefault', 'System Default') : (tpl.name || t('emailTemplates.customTemplate', 'Custom Template'))}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {tpl.is_active && (
                                  <Chip
                                    label={t('emailTemplates.chips.active', 'ACTIVE')}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.65rem',
                                      fontWeight: 900,
                                      backgroundColor: '#dcfce7',
                                      color: '#16a34a',
                                      borderRadius: '6px'
                                    }}
                                  />
                                )}
                                {tpl.is_archived && (
                                  <Chip
                                    label={t('emailTemplates.chips.archived', 'ARCHIVED')}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.65rem',
                                      fontWeight: 900,
                                      backgroundColor: '#f1f5f9',
                                      color: '#64748b',
                                      borderRadius: '6px'
                                    }}
                                  />
                                )}
                              </Box>
                              <Chip
                                label={(tpl.visual_style || 'minimal').split('|')[0].toUpperCase()}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  backgroundColor: '#f1f5f9',
                                  color: '#475569',
                                  borderRadius: '6px'
                                }}
                              />
                            </Box>
                            <Typography sx={{ fontSize: '0.9rem', color: tpl.is_archived ? '#94a3b8' : '#1e293b', fontWeight: 600, mb: 1 }}>
                              Subject: {tpl.subject}
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mb: 1.5, fontStyle: 'italic', lineHeight: 1.5 }}>
                              "{getSnippet(tpl.html_content)}"
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ScheduleIcon sx={{ fontSize: 14 }} /> Updated {tpl.updated_at ? new Date(tpl.updated_at).toLocaleDateString() : 'recently'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 2 }, mt: { xs: 1.5, sm: 0 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                            <Tooltip title="Preview">
                              <IconButton onClick={() => handleOpenPreviewDialog(tpl)} size="small" sx={{ color: '#64748b', border: '1.5px solid #f1f5f9', '&:hover': { backgroundColor: '#f8fafc' } }}>
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!tpl.is_archived && (
                              <Tooltip title="Edit HTML">
                                <IconButton onClick={() => openEditor(tpl, true)} size="small" sx={{ color: '#8b5cf6', border: '1.5px solid rgba(139,92,246,0.1)', '&:hover': { backgroundColor: 'rgba(139,92,246,0.05)' } }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!tpl.is_active && !tpl.is_archived && (
                              <Tooltip title="Set as Active">
                                <IconButton onClick={() => setActive(tpl.id)} size="small" sx={{ color: '#10b981', border: '1.5px solid rgba(16,185,129,0.1)', '&:hover': { backgroundColor: 'rgba(16,185,129,0.05)' } }}>
                                  <StarIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {tpl.is_archived ? (
                              <Tooltip title="Restore Template">
                                <IconButton onClick={() => restoreTemplate(tpl.id)} size="small" sx={{ color: '#10b981', border: '1.5px solid #f1f5f9', '&:hover': { backgroundColor: '#f0fdf4' } }}>
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              !tpl.is_default && (
                                <Tooltip title="Archive Template">
                                  <IconButton onClick={() => archiveTemplate(tpl.id)} size="small" sx={{ color: '#94a3b8', border: '1.5px solid #f1f5f9', '&:hover': { backgroundColor: '#fef3c7', color: '#d97706' } }}>
                                    <ArchiveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )
                            )}
                            {!tpl.is_default && (
                              tpl.is_archived ? (
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={() => deleteTemplate(tpl.id)}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Delete Permanently
                                </Button>
                              ) : (
                                <Tooltip title="Delete Template">
                                  <IconButton onClick={() => deleteTemplate(tpl.id)} size="small" sx={{ color: '#94a3b8', border: '1.5px solid #f1f5f9', '&:hover': { backgroundColor: '#fff1f2', color: '#e11d48' } }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  ))}

                  {viewTab === 1 && templates.filter(t => t.is_archived).length === 0 && (
                    <Box sx={{ py: 8, textAlign: 'center', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #e2e8f0' }}>
                      <ArchiveIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                      <Typography sx={{ color: '#64748b', fontWeight: 600 }}>No archived templates</Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>Templates you archive will appear here.</Typography>
                    </Box>
                  )}

                  {/* Create New Placeholder Card */}
                  {viewTab === 0 && (
                    <Box sx={{ width: '100%' }}>
                      <Paper
                        elevation={0}
                        onClick={isLimitReached ? null : () => openEditor({
                          name: `Custom ${selectedType.label}`,
                          subject: `${selectedType.label}`,
                          html_content: `<p>Hello {{ candidate_name }},</p><p>Start writing your custom template...</p>`,
                          visual_style: 'minimal'
                        }, true)}
                        sx={{
                          p: 3,
                          height: '100%',
                          minHeight: 120,
                          borderRadius: '16px',
                          border: isLimitReached ? '2px solid #f1f5f9' : '2px dashed #e2e8f0',
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 2,
                          cursor: isLimitReached ? 'default' : 'pointer',
                          backgroundColor: isLimitReached ? '#f8fafc' : 'transparent',
                          transition: 'all 0.2s',
                          '&:hover': isLimitReached ? {} : {
                            borderColor: '#8b5cf6',
                            backgroundColor: 'rgba(139,92,246,0.02)',
                            '& .add-icon': { color: '#8b5cf6', transform: 'scale(1.1)' }
                          }
                        }}
                      >
                        {isLimitReached ? (
                          <>
                            <BlockIcon sx={{ fontSize: 24, color: '#94a3b8' }} />
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: '#94a3b8', fontSize: '0.9rem' }}>Limit Reached</Typography>
                              <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>You’ve reached your limit of {MAX_TEMPLATES} templates (including archived). Please permanently delete one to create a new template.</Typography>
                            </Box>
                          </>
                        ) : (
                          <>
                            <AddIcon className="add-icon" sx={{ fontSize: 32, color: '#94a3b8', transition: 'all 0.2s' }} />
                            <Typography sx={{ fontWeight: 700, color: '#94a3b8', fontSize: '0.9rem' }}>Create New Template</Typography>
                          </>
                        )}
                      </Paper>
                    </Box>
                  )}
                </Box>
              ) : (
                /* THE LIST VIEW - "That List" */
                <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '2fr 3fr 1fr 1fr' }, p: 2, background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>TEMPLATE NAME</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', display: { xs: 'none', sm: 'block' } }}>SUBJECT</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', display: { xs: 'none', sm: 'block' } }}>STYLE</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>ACTIONS</Typography>
                  </Box>
                  {viewTab === 0 && defaultTemplate && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', sm: '2fr 3fr 1fr 1fr' },
                        p: 2,
                        borderBottom: '1px solid #f1f5f9',
                        alignItems: 'center',
                        backgroundColor: !templates.some(t => t.is_active) ? '#f0fdf4' : '#fffdfa',
                        borderLeft: !templates.some(t => t.is_active) ? '4px solid #22c55e' : '4px solid #f59e0b',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(124, 58, 237, 0.04)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t('emailTemplates.systemDefault', 'System Default')}
                        </Typography>
                        {!templates.some(t => t.is_active) && <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22c55e', flexShrink: 0 }} title="Active" />}
                        <Chip label="DEFAULT" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: '#fef3c7', color: '#d97706' }} />
                      </Box>
                      <Typography sx={{ color: '#64748b', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: { xs: 'none', sm: 'block' } }}>
                        {defaultTemplate.subject}
                      </Typography>
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Chip label="MINIMAL" size="small" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title={t('emailTemplates.preview', 'Preview')}><IconButton size="small" onClick={() => handleOpenPreviewDialog(defaultTemplate)}><PreviewIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        <Tooltip title={t('emailTemplates.editDefaultTemplate', 'Edit Default Template')}><IconButton size="small" onClick={handleEditDefaultTemplate}><EditIcon sx={{ fontSize: 18, color: '#8b5cf6' }} /></IconButton></Tooltip>
                        {templates.some(t => t.is_active) && (
                          <Tooltip title={t('emailTemplates.setAsActive', 'Set as Active')}><IconButton size="small" onClick={setSystemDefaultActive}><StarIcon sx={{ fontSize: 18, color: '#10b981' }} /></IconButton></Tooltip>
                        )}
                      </Box>
                    </Box>
                  )}
                  {templates.filter(t => (viewTab === 1 ? t.is_archived : !t.is_archived)).map((tpl) => (
                    <Box
                      key={tpl.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', sm: '2fr 3fr 1fr 1fr' },
                        p: 2,
                        borderBottom: '1px solid #f1f5f9',
                        alignItems: 'center',
                        opacity: tpl.is_archived ? 0.7 : 1,
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          background: 'rgba(124, 58, 237, 0.04)',
                          transform: 'scale(1.002)',
                          boxShadow: 'inset 4px 0 0 #7c3aed'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: tpl.is_archived ? '#94a3b8' : '#1e293b', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tpl.name || 'Untitled'}
                        </Typography>
                        {tpl.is_active && <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22c55e', flexShrink: 0 }} title="Active" />}
                        {tpl.is_archived && <Chip label="Archived" size="small" sx={{ height: 16, fontSize: '0.6rem' }} />}
                      </Box>
                      <Typography sx={{ color: '#64748b', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: { xs: 'none', sm: 'block' } }}>
                        {tpl.subject}
                      </Typography>
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Chip label={(tpl.visual_style || 'minimal').split('|')[0].toUpperCase()} size="small" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Preview"><IconButton size="small" onClick={() => handleOpenPreviewDialog(tpl)}><PreviewIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        {!tpl.is_archived && (
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditor(tpl, true)}><EditIcon size="small" sx={{ color: '#8b5cf6' }} /></IconButton></Tooltip>
                        )}
                        {tpl.is_archived ? (
                          <Tooltip title="Restore"><IconButton size="small" onClick={() => restoreTemplate(tpl.id)}><RestoreIcon sx={{ fontSize: 18, color: '#10b981' }} /></IconButton></Tooltip>
                        ) : (
                          !tpl.is_default && (
                            <Tooltip title="Archive"><IconButton size="small" onClick={() => archiveTemplate(tpl.id)}><ArchiveIcon sx={{ fontSize: 18, color: '#f59e0b' }} /></IconButton></Tooltip>
                          )
                        )}
                        {!tpl.is_default && (
                          <Tooltip title={tpl.is_archived ? "Delete Permanently" : "Delete"}><IconButton size="small" onClick={() => deleteTemplate(tpl.id)}><DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} /></IconButton></Tooltip>
                        )}
                      </Box>
                    </Box>
                  ))}
                  {templates.filter(t => (viewTab === 1 ? t.is_archived : !t.is_archived)).length === 0 && (
                    <Box sx={{ p: 6, textAlign: 'center', color: '#94a3b8' }}>
                      <ArchiveIcon sx={{ fontSize: 32, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                      {viewTab === 1 ? "No archived templates found." : "No templates found."}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Fade>
        ) : (
          <Fade in style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Paper elevation={0} sx={{
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              background: 'white',
              overflow: 'hidden',
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
                {/* Editor Header */}
                <Box sx={{
                  px: { xs: 2, md: 2.5 },
                  pt: 1.25,
                  pb: 1,
                  borderBottom: '1px solid #f1f5f9',
                  background: '#f8fafc',
                }}>
                  {/* Single row for Template Name, Chip, Visual Style, and Available Variables */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    flexWrap: 'nowrap',
                    width: '100%',
                    overflowX: 'auto',
                    pt: 1.25,
                    pb: 0.5,
                    '&::-webkit-scrollbar': { display: 'none' }
                  }}>
                    <Button
                      startIcon={<KeyboardBackspaceIcon />}
                      onClick={() => setViewMode('list')}
                      sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600, minWidth: 'auto', p: 0.5, flexShrink: 0 }}
                    />
                    <TextField
                      size="small"
                      inputRef={nameInputRef}
                      required
                      error={nameError && !(editingTemplate.name || '').trim()}
                      helperText={nameError && !(editingTemplate.name || '').trim() ? t('emailTemplates.validation.nameRequired', 'Template Name is required') : ''}
                      InputLabelProps={{
                        shrink: true,
                        sx: {
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          color: '#64748b',
                          '&.Mui-focused': { color: '#2563eb' }
                        }
                      }}
                      FormHelperTextProps={{
                        sx: {
                          position: 'absolute',
                          bottom: -18,
                          left: 0,
                          fontSize: '0.7rem',
                          m: 0,
                          whiteSpace: 'nowrap'
                        }
                      }}
                      label={t('emailTemplates.templateNameLabel', 'Template Name')}
                      placeholder={t('emailTemplates.enterTemplateName', 'Enter template name...')}
                      value={editingTemplate.isSystemDefault ? t('emailTemplates.systemDefault', 'System Default') : (editingTemplate.name ?? '')}
                      onChange={(e) => {
                        if (!editingTemplate.isSystemDefault) {
                          if (nameError && e.target.value.trim()) {
                            setNameError(false);
                          }
                          setEditingTemplate({ ...editingTemplate, name: e.target.value });
                        }
                      }}
                      disabled={editingTemplate.isSystemDefault}
                      sx={{
                        position: 'relative',
                        width: { xs: 160, sm: 200, md: 240 },
                        flexShrink: 0,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          height: 36,
                          fontSize: '0.85rem'
                        }
                      }}
                    />

                    <Chip
                      label={t(`emailTemplates.types.${selectedType.id}.label`, { defaultValue: selectedType.label })}
                      size="small"
                      icon={selectedType.icon}
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        height: 34,
                        px: 1,
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        flexShrink: 0,
                        display: { xs: 'none', sm: 'inline-flex' }
                      }}
                    />

                    {/* Visual Style Dropdown Button */}
                    {(() => {
                      const curStyle = EMAIL_STYLES.find(s => s.id === selectedStyleId) || EMAIL_STYLES[0];
                      return (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => setStyleMenuAnchor(e.currentTarget)}
                            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.8125rem',
                              height: 34,
                              px: 1.5,
                              borderRadius: '8px',
                              flexShrink: 0,
                              whiteSpace: 'nowrap',
                              color: curStyle.color,
                              borderColor: `${curStyle.color}50`,
                              backgroundColor: curStyle.bg || 'white',
                              '&:hover': {
                                borderColor: curStyle.color,
                                backgroundColor: curStyle.bg || '#f8fafc',
                              }
                            }}
                          >
                            <span style={{ marginRight: 6, fontSize: '0.95rem' }}>{curStyle.icon}</span>
                            <span>{curStyle.label}</span>
                          </Button>

                          <Menu
                            anchorEl={styleMenuAnchor}
                            open={Boolean(styleMenuAnchor)}
                            onClose={() => setStyleMenuAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                              sx: {
                                borderRadius: '12px',
                                mt: 0.5,
                                minWidth: 260,
                                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                                border: '1px solid #e2e8f0',
                                p: 0.5,
                              }
                            }}
                          >
                            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f1f5f9' }}>
                              <Typography sx={{ fontSize: '0.725rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.5px' }}>
                                VISUAL STYLE
                              </Typography>
                            </Box>
                            {EMAIL_STYLES.map((style) => {
                              const isSelected = selectedStyleId === style.id;
                              return (
                                <MenuItem
                                  key={style.id}
                                  onClick={() => {
                                    handleStyleChange(style.id);
                                    setStyleMenuAnchor(null);
                                  }}
                                  selected={isSelected}
                                  sx={{
                                    my: 0.25,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.5,
                                    py: 1,
                                    px: 1.5,
                                    backgroundColor: isSelected ? `${style.color}10 !important` : 'transparent',
                                    '&:hover': {
                                      backgroundColor: isSelected ? `${style.color}15 !important` : '#f8fafc',
                                    }
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Typography sx={{ fontSize: '1.2rem', lineHeight: 1 }}>{style.icon}</Typography>
                                    <Box>
                                      <Typography sx={{
                                        fontSize: '0.8125rem',
                                        fontWeight: isSelected ? 700 : 600,
                                        color: isSelected ? style.color : '#1e293b'
                                      }}>
                                        {style.label}
                                      </Typography>
                                      <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                                        {style.description}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {isSelected && (
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: style.color }} />
                                  )}
                                </MenuItem>
                              );
                            })}
                          </Menu>
                        </>
                      );
                    })()}

                    {/* Available Variables Dropdown Button */}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => setVarMenuAnchor(e.currentTarget)}
                      startIcon={<DataObjectIcon sx={{ fontSize: 16, color: '#7c3aed' }} />}
                      endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        height: 34,
                        px: 1.5,
                        borderRadius: '8px',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        color: '#475569',
                        borderColor: '#cbd5e1',
                        backgroundColor: 'white',
                        '&:hover': {
                          borderColor: '#7c3aed',
                          backgroundColor: '#f8fafc',
                          color: '#7c3aed'
                        }
                      }}
                    >
                      Available Variables
                    </Button>

                    <Menu
                      anchorEl={varMenuAnchor}
                      open={Boolean(varMenuAnchor)}
                      onClose={() => setVarMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      PaperProps={{
                        sx: {
                          borderRadius: '12px',
                          mt: 0.5,
                          width: 320,
                          maxHeight: 460,
                          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                          border: '1px solid #e2e8f0',
                          p: 1,
                        }
                      }}
                    >
                      <Box sx={{ px: 1, py: 0.75, mb: 0.5, borderBottom: '1px solid #f1f5f9' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.5px' }}>
                          AVAILABLE VARIABLES
                        </Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                          Click to copy variable to clipboard
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 0.5 }}>
                        {AVAILABLE_VARIABLES
                          .filter(group => group.appliesTo.includes(selectedType.id))
                          .map((group) => (
                            <Box key={group.category}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, mb: 0.5 }}>
                                {group.icon}
                                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#334155' }}>
                                  {group.category}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {group.variables.map((v) => {
                                  const isCopied = copiedVar === v;
                                  return (
                                    <Box
                                      key={v}
                                      onClick={() => {
                                        handleCopyVariable(v);
                                        setCopiedVar(v);
                                        setTimeout(() => setCopiedVar(null), 1500);
                                      }}
                                      sx={{
                                        px: 1.25,
                                        py: 0.6,
                                        borderRadius: '6px',
                                        border: '1px solid',
                                        borderColor: isCopied ? '#86efac' : '#e2e8f0',
                                        background: isCopied ? '#f0fdf4' : '#f8fafc',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                        fontFamily: 'monospace',
                                        color: isCopied ? '#16a34a' : '#475569',
                                        transition: 'all 0.15s',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        userSelect: 'none',
                                        '&:hover': {
                                          background: '#f3e8ff',
                                          borderColor: '#d8b4fe',
                                          color: '#7c3aed'
                                        }
                                      }}
                                    >
                                      <span style={{ fontWeight: 500 }}>{v}</span>
                                      <IconButton
                                        size="small"
                                        sx={{ p: 0.25, color: isCopied ? '#16a34a' : '#94a3b8' }}
                                      >
                                        {isCopied ? (
                                          <CheckCircleIcon sx={{ fontSize: 14, color: '#16a34a' }} />
                                        ) : (
                                          <ContentCopyIcon sx={{ fontSize: 13 }} />
                                        )}
                                      </IconButton>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    </Menu>
                  </Box>

                  {/* Helper text line directly below the single row */}
                  <Typography
                    onClick={() => {
                      const currentTemp = {
                        ...editingTemplate,
                        visual_style: `${selectedStyleId}|${headerColor}|${footerColor}|${emailColor}`
                      };
                      handleOpenPreviewDialog(currentTemp);
                    }}
                    sx={{
                      fontSize: '0.72rem',
                      color: '#64748b',
                      mt: 0.5,
                      ml: { xs: 0, sm: 4.5 },
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': { color: '#7c3aed' }
                    }}
                    title="Click to view real-time preview"
                  >
                    <LightbulbIcon sx={{ fontSize: 13, color: '#8b5cf6' }} />
                    Changes appear instantly in the email template preview.
                  </Typography>
                </Box>

                {/* Subject and Editor Layout - Compact Spacing */}
                <Box sx={{ p: { xs: 2, md: 2.25 }, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'auto' }}>

                  {/* Subject */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <DescriptionOutlinedIcon sx={{ color: '#64748b', fontSize: 18 }} />
                      <Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '0.925rem' }}>Subject</Typography>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      value={editingTemplate.subject}
                      disabled={editingTemplate.isSystemDefault}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                      placeholder="Enter email subject line..."
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          height: 40,
                          '& fieldset': { borderColor: '#e2e8f0', borderWidth: '1px' },
                          '&:hover fieldset': { borderColor: '#cbd5e1' },
                          '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: '2px' }
                        },
                      }}
                    />
                  </Box>

                  {/* Message Body & Toggle */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <DescriptionOutlinedIcon sx={{ color: '#64748b', fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '0.925rem' }}>Message Body</Typography>
                      </Box>

                      <Box sx={{
                        display: 'flex',
                        background: '#f1f5f9',
                        borderRadius: '8px',
                        p: 0.5,
                        gap: 0.5
                      }}>
                        <Button
                          onClick={() => {
                            setActiveTab(0);
                            renderPreviewHtml(
                              editingTemplate.html_content,
                              `${selectedStyleId}|${headerColor}|${footerColor}|${emailColor}|${previewBrandingMode}|${previewHeaderMode}|${previewLogoUrl}`,
                              { header_color: headerColor, footer_color: footerColor, email_color: emailColor },
                              liveVariables,
                              selectedType?.id
                            );
                          }}
                          startIcon={<PreviewIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            minWidth: 'auto',
                            px: 2,
                            py: 0.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            color: activeTab === 0 ? '#1e293b' : '#64748b',
                            background: activeTab === 0 ? 'white' : 'transparent',
                            boxShadow: activeTab === 0 ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            borderRadius: '6px',
                            '&:hover': { background: activeTab === 0 ? 'white' : 'rgba(0,0,0,0.05)' }
                          }}
                        >
                          Preview
                        </Button>
                        {!editingTemplate.isSystemDefault && (
                          <Button
                            onClick={() => setActiveTab(1)}
                            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              minWidth: 'auto',
                              px: 2,
                              py: 0.5,
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              color: activeTab === 1 ? '#2563eb' : '#64748b',
                              background: activeTab === 1 ? '#e0e7ff' : 'transparent',
                              boxShadow: activeTab === 1 ? '0 1px 3px rgba(37, 99, 235, 0.1)' : 'none',
                              borderRadius: '6px',
                              '&:hover': { background: activeTab === 1 ? '#e0e7ff' : 'rgba(0,0,0,0.05)' }
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        <Button
                          onClick={() => {
                            const currentTemp = {
                              ...editingTemplate,
                              visual_style: `${selectedStyleId}|${headerColor}|${footerColor}|${emailColor}|${previewBrandingMode}|${previewHeaderMode}|${previewLogoUrl}`
                            };
                            handleOpenPreviewDialog(currentTemp);
                          }}
                          startIcon={<SettingsOverscanIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            minWidth: 'auto',
                            px: 2,
                            py: 0.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            color: '#7c3aed',
                            background: 'transparent',
                            borderRadius: '6px',
                            '&:hover': { background: '#ede9fe' }
                          }}
                        >
                          Full Preview
                        </Button>
                      </Box>
                    </Box>

                    {/* Editor / Preview Area */}
                    <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
                      {activeTab === 0 ? (
                        <Box sx={{
                          height: '100%',
                          minHeight: 350,
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          overflow: 'auto',
                          backgroundColor: '#f8fafc'
                        }}>
                          <iframe
                            srcDoc={previewHtml}
                            title="Template Preview"
                            style={{ width: '100%', height: '100%', minHeight: 450, border: 'none' }}
                          />
                        </Box>
                      ) : (
                        <Box sx={{
                          minHeight: 480,
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: 'white',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          {/* Editor Mode Switcher Bar */}
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 0.8,
                            background: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0'
                          }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>
                              EDITOR MODE:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.8, background: '#e2e8f0', p: 0.3, borderRadius: '6px' }}>
                              <Button
                                size="small"
                                onClick={handleSwitchToRich}
                                startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  py: 0.2,
                                  px: 1.2,
                                  minWidth: 'auto',
                                  borderRadius: '5px',
                                  color: editorMode === 'rich' ? '#1e293b' : '#64748b',
                                  background: editorMode === 'rich' ? 'white' : 'transparent',
                                  boxShadow: editorMode === 'rich' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                  '&:hover': { background: editorMode === 'rich' ? 'white' : 'rgba(0,0,0,0.05)' }
                                }}
                              >
                                Visual Editor
                              </Button>
                              <Button
                                size="small"
                                onClick={() => {
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    html_content: toFullTemplate(prev.html_content, selectedType.id)
                                  }));
                                  setEditorMode('code');
                                }}
                                startIcon={<CodeIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  py: 0.2,
                                  px: 1.2,
                                  minWidth: 'auto',
                                  borderRadius: '5px',
                                  color: editorMode === 'code' ? '#2563eb' : '#64748b',
                                  background: editorMode === 'code' ? 'white' : 'transparent',
                                  boxShadow: editorMode === 'code' ? '0 1px 2px rgba(37, 99, 235, 0.1)' : 'none',
                                  '&:hover': { background: editorMode === 'code' ? 'white' : 'rgba(0,0,0,0.05)' }
                                }}
                              >
                                HTML Code
                              </Button>
                            </Box>
                          </Box>
                          {editorMode === 'code' && (
                            <Box sx={{ px: 2, py: 0.8, bgcolor: '#eff6ff', borderBottom: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LightbulbIcon sx={{ color: '#2563eb', fontSize: 18 }} />
                              <Typography sx={{ fontSize: '0.8rem', color: '#1e40af', fontWeight: 500 }}>
                                <strong>HTML Code Mode:</strong> Edit template text or remove sections safely here without losing card styling or rounded borders. Click <strong>Preview</strong> above to see your changes live.
                              </Typography>
                            </Box>
                          )}
                          {editorMode === 'code' ? (
                            <Editor
                              height="480px"
                              language="html"
                              theme="vs-light"
                              value={editingTemplate.html_content || ''}
                              onChange={(val) => setEditingTemplate(prev => ({ ...prev, html_content: val || '' }))}
                              options={{
                                minimap: { enabled: false },
                                wordWrap: 'on',
                                fontSize: 13,
                                scrollBeyondLastLine: false,
                                automaticLayout: true,
                                tabSize: 2
                              }}
                            />
                          ) : (
                            <RichTextEditor
                              value={editingTemplate.html_content}
                              onChange={(val) => setEditingTemplate({ ...editingTemplate, html_content: val })}
                              placeholder="Enter your email content here..."
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Compact Footer Actions */}
                <Box sx={{
                  px: 2.5,
                  py: 1.25,
                  borderTop: '1px solid #f1f5f9',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f8fafc',
                  mt: 'auto'
                }}>
                  <Button
                    variant="outlined"
                    onClick={() => setViewMode('list')}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      height: 34,
                      px: 2.5,
                      backgroundColor: 'white',
                      borderColor: '#e2e8f0',
                      color: '#64748b',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      '&:hover': {
                        background: '#f8fafc',
                        borderColor: '#cbd5e1'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon sx={{ transform: 'rotate(-45deg)', fontSize: 16 }} />}
                    onClick={() => handleSave()}
                    disabled={saving || editingTemplate.isSystemDefault}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      height: 34,
                      px: 2.5,
                      background: '#2563eb',
                      boxShadow: '0 2px 4px -1px rgba(37, 99, 235, 0.2)',
                      '&:hover': {
                        background: '#1d4ed8',
                        boxShadow: '0 4px 8px -2px rgba(37, 99, 235, 0.3)'
                      }
                    }}
                  >
                    Save Template
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Fade>
        )}
      </Box>


    </Box>
  );

  const dialogs = (
    <>
      <DynamicBasicDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        content={confirmDialog.content}
        confirmButtonText={confirmDialog.confirmButtonText}
      />

      {/* Centered Gmail-Style Split-Panel Modal Preview */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: '1400px',
            height: '92vh',
            display: 'flex',
            flexDirection: 'row',
            backgroundColor: 'white',
            boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.18)'
          }
        }}
      >
        {/* Left Section - Email Template Preview (60% width) */}
        <Box sx={{
          flex: 1.5,
          backgroundColor: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          borderRight: '1px solid #cbd5e1'
        }}>
          {/* Header - Compact Space */}
          <Box sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid #cbd5e1', backgroundColor: 'white', flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f172a', letterSpacing: '-0.01em', mb: 0.1 }}>
              Email Template Preview
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
              See how your email looks across different themes and devices.
            </Typography>
          </Box>

          {/* Iframe Preview Container Area */}
          <Box
            className="preview-scroll-area"
            sx={{
              flex: 1,
              minHeight: 0,
              px: { xs: 2, sm: 2.5 },
              pt: 1.25,
              pb: 1.5,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              backgroundColor: '#f8fafc'
            }}
          >
            {/* Viewport & Email Client Selection - Single Row, 2 Columns */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'nowrap',
              width: '100%',
              flexShrink: 0,
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              mb: 0.5
            }}>
              {/* Column 1: Live Preview */}
              <Box sx={{ flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#64748b', mb: 0.3, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Live Preview
                </Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, backgroundColor: 'white', p: 0.25, borderRadius: '8px', border: '1px solid #cbd5e1', height: 30, boxSizing: 'border-box' }}>
                  {[
                    { id: 'desktop', label: 'Desktop', icon: <DesktopWindowsIcon sx={{ fontSize: 15 }} /> },
                    { id: 'tablet', label: 'Tablet', icon: <TabletIcon sx={{ fontSize: 15 }} /> },
                    { id: 'mobile', label: 'Mobile', icon: <SmartphoneIcon sx={{ fontSize: 15 }} /> }
                  ].map((device) => {
                    const isSelected = previewDevice === device.id;
                    return (
                      <Button
                        key={device.id}
                        variant="text"
                        onClick={() => setPreviewDevice(device.id)}
                        startIcon={device.icon}
                        sx={{
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          height: 24,
                          px: 1,
                          minWidth: 'auto',
                          color: isSelected ? '#2563eb' : '#64748b',
                          backgroundColor: isSelected ? 'rgba(37,99,235,0.08)' : 'transparent',
                          '&:hover': {
                            backgroundColor: isSelected ? 'rgba(37,99,235,0.12)' : 'rgba(0,0,0,0.03)'
                          }
                        }}
                      >
                        {device.label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>

              {/* Column 2: Email Client */}
              <Box sx={{ flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#64748b', mb: 0.3, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Email Client
                </Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, backgroundColor: 'white', p: 0.25, borderRadius: '8px', border: '1px solid #cbd5e1', height: 30, boxSizing: 'border-box' }}>
                  {[
                    { id: 'gmail', label: 'Gmail', icon: <span style={{ color: '#ea4335', fontWeight: 'bold', marginRight: 2, display: 'inline-flex', alignItems: 'center', height: 14, lineHeight: 1 }}>✉</span> },
                    { id: 'outlook', label: 'Outlook', icon: <span style={{ color: '#0078d4', fontWeight: 'bold', marginRight: 2, display: 'inline-flex', alignItems: 'center', height: 14, lineHeight: 1 }}>🟦</span> },
                    { id: 'apple', label: 'Apple Mail', icon: <span style={{ color: '#000', fontWeight: 'bold', marginRight: 2, display: 'inline-flex', alignItems: 'center', height: 14, lineHeight: 1 }}>📬</span> },
                    { id: 'yahoo', label: 'Yahoo Mail', icon: <span style={{ color: '#6001d2', fontWeight: 'bold', marginRight: 2, display: 'inline-flex', alignItems: 'center', height: 14, lineHeight: 1 }}>🟣</span> }
                  ].map((client) => {
                    const isSelected = previewClient === client.id;
                    return (
                      <Button
                        key={client.id}
                        variant="text"
                        onClick={() => setPreviewClient(client.id)}
                        startIcon={client.icon}
                        sx={{
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          height: 24,
                          px: 1,
                          minWidth: 'auto',
                          color: isSelected ? '#7c3aed' : '#64748b',
                          backgroundColor: isSelected ? 'rgba(124,58,237,0.08)' : 'transparent',
                          '&:hover': {
                            backgroundColor: isSelected ? 'rgba(124,58,237,0.12)' : 'rgba(0,0,0,0.03)'
                          }
                        }}
                      >
                        {client.label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* Envelope Container Mockup - Compact & Fits Viewport */}
            <Box sx={{
              borderRadius: '12px',
              border: '1px solid #cbd5e1',
              overflow: 'hidden',
              backgroundColor: 'white',
              boxShadow: '0 4px 15px -3px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              width: previewDevice === 'desktop' ? '100%' : previewDevice === 'tablet' ? '680px' : '380px',
              maxWidth: '100%',
              flex: 1,
              minHeight: 0,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              alignSelf: 'center'
            }}>
              {/* Conditional Headers by previewClient */}
              {previewClient === 'gmail' && (
                <>
                  {/* Gmail Header */}
                  <Box sx={{
                    backgroundColor: '#f1f3f4',
                    borderBottom: '1px solid #e0e0e0',
                    px: 2, py: 0.7,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography sx={{ fontWeight: 800, color: '#ea4335', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5, fontFamily: 'sans-serif' }}>
                        <span style={{ fontSize: '1.2rem', color: '#ea4335' }}>✉</span> Gmail
                      </Typography>
                      <Box sx={{
                        backgroundColor: 'white',
                        borderRadius: '24px',
                        px: 2, py: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        width: { xs: 150, sm: 220, md: 280 },
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <span style={{ color: '#5f6368', fontSize: '0.8rem', marginRight: 8 }}>🔍</span>
                        <input type="text" placeholder="Search mail" style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.78rem' }} disabled />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#5f6368', fontSize: '1rem' }}>
                      <span>❓</span>
                      <span>⚙️</span>
                      <span>⠿</span>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700 }}>
                        A
                      </Box>
                    </Box>
                  </Box>

                  {/* Gmail subheader */}
                  <Box sx={{ px: 2.5, py: 0.7, borderBottom: '1px solid #f1f3f4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <span style={{ cursor: 'pointer', color: '#5f6368' }}>←</span>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#202124' }}>
                        Interview Invitation for Senior Software Engineer
                      </Typography>
                      <Box sx={{ backgroundColor: '#f1f3f4', color: '#5f6368', fontSize: '0.65rem', px: 1, py: 0.2, borderRadius: '4px', fontWeight: 600 }}>
                        External
                      </Box>
                      <Box sx={{ backgroundColor: '#f1f3f4', color: '#5f6368', fontSize: '0.65rem', px: 1, py: 0.2, borderRadius: '4px', fontWeight: 600 }}>
                        Inbox x
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#5f6368', fontSize: '1rem' }}>
                      <span>☆</span>
                      <span>↩</span>
                      <span>⋮</span>
                    </Box>
                  </Box>

                  {/* Sender details */}
                  <Box sx={{ px: 2.5, py: 0.7, display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem' }}>
                      A
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#202124' }}>
                          AptiView
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#5f6368' }}>
                          &lt;no-reply@aptiview.ai&gt;
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.72rem', color: '#5f6368' }}>
                        to {liveVariables.candidate_name || 'John Doe'} ▾
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: '#5f6368' }}>
                      10:00 AM (2 minutes ago)
                    </Typography>
                  </Box>
                </>
              )}

              {previewClient === 'outlook' && (
                <>
                  {/* Outlook Header */}
                  <Box sx={{
                    backgroundColor: '#0078d4',
                    color: 'white',
                    px: 2, py: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>🟦</span>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', fontFamily: 'sans-serif' }}>
                        Outlook
                      </Typography>
                      <Box sx={{
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        borderRadius: '4px',
                        px: 2, py: 0.3,
                        display: 'flex',
                        alignItems: 'center',
                        width: { xs: 150, sm: 220, md: 280 }
                      }}>
                        <span style={{ color: 'white', fontSize: '0.8rem', marginRight: 8 }}>🔍</span>
                        <input type="text" placeholder="Search" style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.78rem', background: 'transparent', color: 'white' }} disabled />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: '0.9rem' }}>
                      <span>🔔</span>
                      <span>⚙️</span>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#005a9e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700 }}>
                        A
                      </Box>
                    </Box>
                  </Box>

                  {/* Outlook Sender details */}
                  <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f3f4', display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#323130' }}>
                      Interview Invitation for Senior Software Engineer
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#0078d4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem' }}>
                        AV
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#323130' }}>
                            AptiView &lt;no-reply@aptiview.ai&gt;
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#605e5c' }}>
                            Thu 6/25/2026 10:00 AM
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.75rem', color: '#605e5c' }}>
                          To: {liveVariables.candidate_name || 'John Doe'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              {previewClient === 'apple' && (
                <>
                  {/* Apple Mail Header */}
                  <Box sx={{
                    backgroundColor: '#f6f6f6',
                    borderBottom: '1px solid #dcdcdc',
                    px: 3, py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '1.2rem' }}>📬</span>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#4a4a4a', fontFamily: '-apple-system' }}>
                        Inbox — AptiView
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, color: '#6d6d6d' }}>
                      <span>📥</span>
                      <span>📤</span>
                      <span>🗑️</span>
                    </Box>
                  </Box>

                  {/* Apple Mail envelope fields */}
                  <Box sx={{ px: 4, py: 2, borderBottom: '1px solid #e9e9e9', display: 'flex', flexDirection: 'column', gap: 0.6, width: '100%', backgroundColor: '#fafafa' }}>
                    <Box sx={{ display: 'flex', fontSize: '0.8rem' }}>
                      <Typography sx={{ fontWeight: 700, width: 70, color: '#8e8e93' }}>From:</Typography>
                      <Typography sx={{ color: '#1d1d1f', fontWeight: 600 }}>AptiView &lt;no-reply@aptiview.ai&gt;</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', fontSize: '0.8rem' }}>
                      <Typography sx={{ fontWeight: 700, width: 70, color: '#8e8e93' }}>Subject:</Typography>
                      <Typography sx={{ color: '#1d1d1f', fontWeight: 700 }}>Interview Invitation for Senior Software Engineer</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', fontSize: '0.8rem' }}>
                      <Typography sx={{ fontWeight: 700, width: 70, color: '#8e8e93' }}>Date:</Typography>
                      <Typography sx={{ color: '#1d1d1f' }}>June 25, 2026 at 10:00 AM GMT+5:30</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', fontSize: '0.8rem' }}>
                      <Typography sx={{ fontWeight: 700, width: 70, color: '#8e8e93' }}>To:</Typography>
                      <Typography sx={{ color: '#1d1d1f' }}>{liveVariables.candidate_name || 'John Doe'}</Typography>
                    </Box>
                  </Box>
                </>
              )}

              {previewClient === 'yahoo' && (
                <>
                  {/* Yahoo Mail Header */}
                  <Box sx={{
                    backgroundColor: '#6001d2',
                    color: 'white',
                    px: 3, py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', letterSpacing: '-0.5px' }}>
                        yahoo!
                      </Typography>
                      <Box sx={{
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        px: 1.5, py: 0.3,
                        display: 'flex',
                        alignItems: 'center',
                        width: { xs: 150, sm: 220, md: 280 }
                      }}>
                        <input type="text" placeholder="Search mail" style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.78rem' }} disabled />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <span>⚙️</span>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold' }}>
                        Y
                      </Box>
                    </Box>
                  </Box>

                  {/* Yahoo Mail sender area */}
                  <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e0e4e9', display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#6001d2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.8rem' }}>
                          A
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1d2a3a' }}>
                            AptiView
                          </Typography>
                          <Typography sx={{ fontSize: '0.7' }}>
                            &lt;no-reply@aptiview.ai&gt;
                          </Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: '0.7rem', color: '#5b6b7c' }}>
                        10:00 AM
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1d2a3a', mt: 1 }}>
                      Interview Invitation for Senior Software Engineer
                    </Typography>
                  </Box>
                </>
              )}

              {/* Safe iframe preview wrapper - Fills remaining space */}
              <Box sx={{
                flex: 1,
                minHeight: 0,
                backgroundColor: '#f8fafc',
                p: { xs: 1, sm: 1.5 },
                borderTop: '1px solid #cbd5e1',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}>
                <SafeIframePreview html={previewHtml} title="Mail Preview Frame" />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Section - Settings & Controls (40% width) */}
        <Box sx={{
          flex: 1,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
            borderBottom: '1px solid #cbd5e1',
            backgroundColor: 'white'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '1.25rem' }}>⚙️</span>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', fontFamily: "'Poppins', sans-serif" }}>
                Template Settings
              </Typography>
            </Box>
            <IconButton onClick={() => setPreviewDialogOpen(false)} sx={{ color: '#64748b', '&:hover': { backgroundColor: '#f1f5f9', color: '#ef4444' } }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Controls List Scroll Area */}
          <Box sx={{ p: { xs: 2, sm: 2.5 }, overflowY: 'auto', flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* 1. Choose Template */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', mb: 2, fontFamily: "'Poppins', sans-serif" }}>
                1. Choose Template
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.5,
                overflowX: 'auto',
                pb: 1.5,
                width: '100%',
                '&::-webkit-scrollbar': { height: '5px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '10px' }
              }}>
                {[
                  {
                    id: 'minimal',
                    label: 'Minimalist Clean',
                    color: '#10b981',
                    renderThumbnail: (brandColor) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: 'white' }}>
                        <Box sx={{ height: 2.5, width: '100%', backgroundColor: brandColor }} />
                        <Box sx={{ p: 0.6, display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 0.4 }}>
                          {/* Header */}
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.1 }}>
                            <Box sx={{ fontSize: '5px', fontWeight: 'bold', color: '#1e293b', scale: '0.8' }}>AptiView</Box>
                          </Box>
                          <Divider sx={{ borderColor: '#f1f5f9', my: 0.1 }} />
                          {/* Content Area */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.2 }}>
                            <Box sx={{ height: 1.5, width: '70%', backgroundColor: '#cbd5e1', borderRadius: 0.2 }} />
                            <Box sx={{ height: 1.5, width: '90%', backgroundColor: '#cbd5e1', borderRadius: 0.2 }} />
                          </Box>
                          {/* CTA Button */}
                          <Box sx={{ alignSelf: 'center', mt: 0.8, height: 6, width: 30, borderRadius: '2px', border: `0.5px solid ${brandColor}`, backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ height: 1, width: 10, backgroundColor: brandColor }} />
                          </Box>
                          {/* Footer */}
                          <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, py: 0.2, borderTop: '0.5px solid #f1f5f9' }}>
                            <Box sx={{ height: 1, width: 40, backgroundColor: '#cbd5e1' }} />
                          </Box>
                        </Box>
                      </Box>
                    )
                  },
                  {
                    id: 'modern_card',
                    label: 'Modern Professional',
                    color: '#2563eb',
                    popular: true,
                    renderThumbnail: (brandColor) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: '#f8fafc', p: 0.4 }}>
                        <Box sx={{ flexGrow: 1, backgroundColor: 'white', borderRadius: '3px', border: '0.5px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                          {/* Header */}
                          <Box sx={{ px: 0.4, py: 0.2, borderBottom: '0.5px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
                              <Box sx={{ width: 2, height: 2, borderRadius: '50%', backgroundColor: brandColor }} />
                              <Box sx={{ fontSize: '4.5px', fontWeight: 800, color: '#0f172a', scale: '0.75' }}>AptiView</Box>
                            </Box>
                            <Box sx={{ height: 1.5, width: 10, backgroundColor: `${brandColor}22`, borderRadius: 0.5 }} />
                          </Box>
                          {/* Content Area */}
                          <Box sx={{ p: 0.4, display: 'flex', flexDirection: 'column', gap: 0.3, flexGrow: 1 }}>
                            <Box sx={{ height: 2.5, width: '60%', backgroundColor: '#0f172a', borderRadius: 0.2 }} />
                            <Box sx={{ height: 1.5, width: '90%', backgroundColor: '#f1f5f9', borderRadius: 0.2 }} />
                            <Box sx={{ height: 1.5, width: '80%', backgroundColor: '#f1f5f9', borderRadius: 0.2 }} />
                            {/* CTA Button */}
                            <Box sx={{ alignSelf: 'center', mt: 0.5, height: 6, width: 32, borderRadius: '1.5px', backgroundColor: brandColor }} />
                          </Box>
                          {/* Footer */}
                          <Box sx={{ p: 0.2, backgroundColor: '#f8fafc', borderTop: '0.5px solid #f1f5f9', display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ height: 1, width: 35, backgroundColor: '#cbd5e1' }} />
                          </Box>
                        </Box>
                      </Box>
                    )
                  },
                  {
                    id: 'formal',
                    label: 'Executive Formal',
                    color: '#0f172a',
                    renderThumbnail: (brandColor) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: '#f1f5f9', p: 0.4 }}>
                        <Box sx={{ flexGrow: 1, backgroundColor: 'white', borderRadius: '2px', borderTop: `2.5px solid ${brandColor}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                          {/* Header */}
                          <Box sx={{ px: 0.4, py: 0.3, borderBottom: '0.5px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.1 }}>
                            <Box sx={{ fontSize: '5px', fontWeight: 'bold', color: '#0f172a', letterSpacing: '0.5px', scale: '0.8' }}>APTIVIEW</Box>
                            <Box sx={{ height: 1, width: 30, backgroundColor: '#cbd5e1' }} />
                          </Box>
                          {/* Content Area */}
                          <Box sx={{ p: 0.4, display: 'flex', flexDirection: 'column', gap: 0.3, flexGrow: 1 }}>
                            <Box sx={{ height: 1.5, width: '90%', backgroundColor: '#475569' }} />
                            <Box sx={{ height: 1.5, width: '85%', backgroundColor: '#475569' }} />
                            <Box sx={{ height: 1.5, width: '90%', backgroundColor: '#475569' }} />
                            <Box sx={{ alignSelf: 'center', mt: 0.4, height: 5, width: 28, border: '0.5px solid #0f172a', borderRadius: '1px' }} />
                          </Box>
                          {/* Footer */}
                          <Box sx={{ p: 0.3, backgroundColor: '#f8fafc', borderTop: '0.5px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                            <Box sx={{ height: 1, width: '90%', backgroundColor: '#cbd5e1' }} />
                            <Box sx={{ height: 1, width: '70%', backgroundColor: '#cbd5e1' }} />
                          </Box>
                        </Box>
                      </Box>
                    )
                  },
                  {
                    id: 'creative',
                    label: 'Enterprise Structured',
                    color: '#f59e0b',
                    renderThumbnail: (brandColor) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: '#f1f5f9', p: 0.4 }}>
                        <Box sx={{ flexGrow: 1, backgroundColor: 'white', borderRadius: '4px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                          {/* Header Banner */}
                          <Box sx={{ backgroundColor: brandColor, py: 0.3, px: 0.4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ fontSize: '5px', fontWeight: '900', color: 'white', scale: '0.8' }}>AptiView</Box>
                            <Box sx={{ height: 1.5, width: 8, backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: 0.5 }} />
                          </Box>
                          {/* Content Area */}
                          <Box sx={{ p: 0.4, display: 'flex', flexDirection: 'column', gap: 0.3, flexGrow: 1 }}>
                            <Box sx={{ height: 1.5, width: '60%', backgroundColor: '#0f172a' }} />
                            {/* Nested Info Card */}
                            <Box sx={{ backgroundColor: '#f8fafc', border: '0.5px solid #e2e8f0', borderRadius: '2px', p: 0.3, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                              <Box sx={{ height: 1, width: '50%', backgroundColor: brandColor }} />
                              <Box sx={{ height: 1, width: '80%', backgroundColor: '#cbd5e1' }} />
                            </Box>
                            {/* CTA button */}
                            <Box sx={{ alignSelf: 'center', mt: 'auto', height: 5, width: 30, backgroundColor: brandColor, borderRadius: '1.5px' }} />
                          </Box>
                          {/* Footer */}
                          <Box sx={{ py: 0.2, px: 0.4, backgroundColor: '#f8fafc', borderTop: '0.5px solid #cbd5e1', display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ height: 1, width: 30, backgroundColor: '#cbd5e1' }} />
                          </Box>
                        </Box>
                      </Box>
                    )
                  },
                  {
                    id: 'compact',
                    label: 'Compact Direct',
                    color: '#ec4899',
                    renderThumbnail: (brandColor) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: 'white' }}>
                        <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.3, flexGrow: 1 }}>
                          {/* Header */}
                          <Box sx={{ borderBottom: '0.5px solid #f1f5f9', pb: 0.2 }}>
                            <Box sx={{ fontSize: '4.5px', color: '#64748b', fontWeight: 'bold', scale: '0.85', transformOrigin: 'top left' }}>From: AptiView</Box>
                          </Box>
                          {/* Body with vertical left border */}
                          <Box sx={{ borderLeft: `2.5px solid ${brandColor}`, pl: 0.4, display: 'flex', flexDirection: 'column', gap: 0.3, my: 0.1, flexGrow: 1 }}>
                            <Box sx={{ fontSize: '5px', fontWeight: 'bold', color: '#0f172a', scale: '0.8', transformOrigin: 'top left' }}>Hello John,</Box>
                            <Box sx={{ height: 1.5, width: '100%', backgroundColor: '#e2e8f0', borderRadius: 0.2 }} />
                            <Box sx={{ height: 1.5, width: '80%', backgroundColor: '#e2e8f0', borderRadius: 0.2 }} />
                            {/* CTA */}
                            <Box sx={{ height: 5, width: 22, backgroundColor: brandColor, borderRadius: '1.5px', mt: 'auto' }} />
                          </Box>
                          {/* Footer */}
                          <Box sx={{ pt: 0.1, borderTop: '0.5px solid #f1f5f9', display: 'flex' }}>
                            <Box sx={{ height: 1, width: 25, backgroundColor: '#cbd5e1' }} />
                          </Box>
                        </Box>
                      </Box>
                    )
                  }
                ].map((style) => {
                  const isSelected = previewStyleId === style.id;
                  return (
                    <Box
                      key={style.id}
                      onClick={() => handleDialogStyleChange(style.id)}
                      sx={{
                        width: 145,
                        flexShrink: 0,
                        p: 1,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        border: isSelected ? `2.5px solid ${style.color}` : '1.5px solid #e2e8f0',
                        backgroundColor: 'white',
                        transition: 'all 0.2s',
                        position: 'relative',
                        boxShadow: isSelected ? '0 4px 10px rgba(0,0,0,0.05)' : 'none',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          borderColor: isSelected ? style.color : '#cbd5e1'
                        }
                      }}
                    >
                      {isSelected && (
                        <Box sx={{ position: 'absolute', top: 6, right: 6, px: 0.8, py: 0.2, backgroundColor: style.color, color: 'white', fontSize: '0.55rem', fontWeight: 900, borderRadius: '3px', zIndex: 2 }}>
                          ✓ SELECTED
                        </Box>
                      )}
                      {style.popular && !isSelected && (
                        <Box sx={{ position: 'absolute', top: 6, right: 6, px: 0.8, py: 0.2, backgroundColor: 'rgba(37,99,235,0.08)', color: '#2563eb', fontSize: '0.55rem', fontWeight: 900, borderRadius: '3px', zIndex: 2 }}>
                          POPULAR
                        </Box>
                      )}

                      {/* Mini visual mockup box */}
                      <Box sx={{
                        height: 80,
                        border: '1px solid #e2e8f0',
                        backgroundColor: 'white',
                        mb: 1,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        {style.renderThumbnail(previewEmailColor)}
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#1e293b' }}>{style.label}</Typography>
                    </Box>
                  );
                })}
              </Box>
              <Typography sx={{ fontSize: '0.72rem', color: '#2563eb', fontWeight: 700, mt: 1.5, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                View All Templates →
              </Typography>
            </Box>

            {/* 2. Brand Color */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', mb: 2, fontFamily: "'Poppins', sans-serif" }}>
                2. Brand Color
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                {[
                  { name: 'AptiView Purple', hex: '#7c3aed' },
                  { name: 'Corporate Blue', hex: '#2563eb' },
                  { name: 'Emerald Hiring', hex: '#10b981' },
                  { name: 'Teal Accent', hex: '#0d9488' },
                  { name: 'Energetic Orange', hex: '#f97316' },
                  { name: 'Creative Pink', hex: '#f43f5e' },
                  { name: 'Executive Navy', hex: '#0f172a' }
                ].map((color) => {
                  const isSelected = previewEmailColor === color.hex;
                  return (
                    <Tooltip key={color.hex} title={color.name} arrow>
                      <Box
                        onClick={() => handleDialogColorChange('email', color.hex)}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: color.hex,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid transparent',
                          boxShadow: isSelected ? `0 0 0 2.5px white, 0 0 0 4.5px ${color.hex}` : '0 1px 3px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        {isSelected && <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}>✓</span>}
                      </Box>
                    </Tooltip>
                  );
                })}

                {/* Custom Color Circle */}
                <Tooltip title="Custom Color" arrow>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      border: '1.5px solid #cbd5e1',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        borderColor: '#2563eb'
                      }
                    }}
                  >
                    <span style={{ fontSize: '1rem', color: '#64748b', fontWeight: 'bold' }}>+</span>
                    <input
                      type="color"
                      value={previewEmailColor}
                      onChange={(e) => handleDialogColorChange('email', e.target.value)}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                    />
                  </Box>
                </Tooltip>
              </Box>

              {/* Brand preview area */}
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Brand preview
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: previewEmailColor }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b' }}>
                    {liveVariables.company_name || 'AptiView'}
                  </Typography>
                  <Box sx={{ width: 100, height: 4, borderRadius: 2, backgroundColor: `${previewEmailColor}22` }}>
                    <Box sx={{ width: '60%', height: '100%', borderRadius: 2, backgroundColor: previewEmailColor }} />
                  </Box>
                </Box>
                <Button
                  size="small"
                  onClick={() => handleDialogColorChange('email', '#7c3aed')} // reset to default purple
                  startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#7c3aed',
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': { background: 'transparent', textDecoration: 'underline' }
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Box>

            {/* 3. Company Branding */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', mb: 2, fontFamily: "'Poppins', sans-serif" }}>
                3. Company Branding
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Logo upload display */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                    Company Logo
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {previewLogoUrl ? (
                      <Box sx={{ position: 'relative', border: '1px solid #e2e8f0', p: 1, borderRadius: '4px', backgroundColor: '#f8fafc' }}>
                        <img src={getFullMediaUrl(previewLogoUrl)} alt="Logo Preview" style={{ maxHeight: 32, maxWidth: 120, objectFit: 'contain' }} />
                      </Box>
                    ) : (
                      <Box sx={{
                        width: 48,
                        height: 32,
                        borderRadius: '4px',
                        border: '1.5px dashed #cbd5e1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8fafc',
                        color: '#94a3b8',
                        fontSize: '0.65rem',
                        fontWeight: 700
                      }}>
                        No Logo
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <input
                        accept="image/*"
                        id="logo-upload-file"
                        type="file"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('file', file);
                            try {
                              const uploadResp = await api.post('/email-templates/upload_media/', formData, {
                                headers: {
                                  'Content-Type': 'multipart/form-data'
                                }
                              });
                              if (uploadResp.data && uploadResp.data.url) {
                                handleDialogLogoChange(uploadResp.data.url);
                              }
                            } catch (err) {
                              console.error('Logo upload failed:', err);
                              toast.error('Failed to upload logo. Please try again.');
                            }
                          }
                        }}
                      />
                      <label htmlFor="logo-upload-file">
                        <Button
                          variant="outlined"
                          component="span"
                          size="small"
                          sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700, py: 0.5 }}
                        >
                          {previewLogoUrl ? 'Change' : 'Upload'}
                        </Button>
                      </label>
                      {previewLogoUrl && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDialogLogoChange('')}
                          sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700, py: 0.5 }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Branding Mode Selector */}
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
                    Branding Mode
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                    {[
                      { id: 'co_branded', label: 'Co-Branded', subtitle: 'Powered by AptiView' },
                      { id: 'white_label', label: 'White-Label', subtitle: 'Your Brand Only' }
                    ].map((mode) => {
                      const isSelected = previewBrandingMode === mode.id;
                      return (
                        <Button
                          key={mode.id}
                          variant="outlined"
                          onClick={() => handleDialogBrandingModeChange(mode.id)}
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 1,
                            px: 1.5,
                            color: isSelected ? '#7c3aed' : '#64748b',
                            borderColor: isSelected ? '#7c3aed' : '#e2e8f0',
                            backgroundColor: isSelected ? 'rgba(124,58,237,0.04)' : 'transparent',
                            borderWidth: isSelected ? '2px' : '1px',
                            '&:hover': {
                              borderColor: isSelected ? '#7c3aed' : '#cbd5e1',
                              backgroundColor: isSelected ? 'rgba(124,58,237,0.08)' : '#f8fafc'
                            }
                          }}
                        >
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{mode.label}</Typography>
                          <Typography sx={{ fontSize: '0.62rem', opacity: 0.8 }}>{mode.subtitle}</Typography>
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                {/* Header Mode Controls */}
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
                    Header Layout Mode
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                    {[
                      { id: 'logo_and_text', label: 'Logo + Sub' },
                      { id: 'logo_only', label: 'Logo Only' },
                      { id: 'full_banner', label: 'Full Banner' }
                    ].map((mode) => {
                      const isSelected = previewHeaderMode === mode.id;
                      return (
                        <Button
                          key={mode.id}
                          variant="outlined"
                          onClick={() => handleDialogHeaderModeChange(mode.id)}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 0.8,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: isSelected ? '#7c3aed' : '#64748b',
                            borderColor: isSelected ? '#7c3aed' : '#e2e8f0',
                            backgroundColor: isSelected ? 'rgba(124,58,237,0.04)' : 'transparent',
                            borderWidth: isSelected ? '1.5px' : '1px',
                            '&:hover': {
                              borderColor: isSelected ? '#7c3aed' : '#cbd5e1',
                              backgroundColor: isSelected ? 'rgba(124,58,237,0.08)' : '#f8fafc'
                            }
                          }}
                        >
                          {mode.label}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Support Email"
                    value={liveVariables.support_email || ''}
                    onChange={(e) => handleDialogVariableChange('support_email', e.target.value)}
                    InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                    inputProps={{ style: { fontSize: '0.8rem' } }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Company Name"
                    value={liveVariables.company_name || ''}
                    onChange={(e) => handleDialogVariableChange('company_name', e.target.value)}
                    InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                    inputProps={{ style: { fontSize: '0.8rem' } }}
                  />
                </Box>
              </Box>
            </Box>

          </Box>

          {/* Action Row Footer - Slim & Uniform Height */}
          <Box sx={{
            px: 2,
            py: 1.25,
            borderTop: '1px solid #cbd5e1',
            display: 'flex',
            gap: 1.25,
            backgroundColor: 'white',
            flexShrink: 0
          }}>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              startIcon={<SendIcon sx={{ fontSize: 15 }} />}
              onClick={handleSendTestEmail}
              sx={{
                height: 34,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                color: '#475569',
                borderColor: '#cbd5e1',
                whiteSpace: 'nowrap',
                px: 1,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                '&:hover': {
                  borderColor: '#94a3b8',
                  backgroundColor: '#f8fafc'
                }
              }}
            >
              Send Test Email
            </Button>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
              onClick={handleCopyPreviewHtml}
              sx={{
                height: 34,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                color: '#475569',
                borderColor: '#cbd5e1',
                whiteSpace: 'nowrap',
                px: 1,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                '&:hover': {
                  borderColor: '#94a3b8',
                  backgroundColor: '#f8fafc'
                }
              }}
            >
              Copy HTML
            </Button>
            <Button
              variant="contained"
              fullWidth
              size="small"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 15 }} />}
              onClick={async () => {
                const isStyleChanged = previewStyleId !== 'minimal' || previewEmailColor !== '#2563eb' || previewHeaderColor !== '#2563eb' || Boolean(previewLogoUrl) || previewHeaderMode !== 'logo_and_text' || previewBrandingMode !== 'co_branded';
                if (previewTemplate?.isSystemDefault && !isStyleChanged) {
                  await setSystemDefaultActive();
                  setPreviewDialogOpen(false);
                } else {
                  const ok = await handleSave(true);
                  if (ok) {
                    setPreviewDialogOpen(false);
                  }
                }
              }}
              sx={{
                height: 34,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                backgroundColor: '#7c3aed',
                color: 'white',
                whiteSpace: 'nowrap',
                px: 1,
                boxShadow: '0 2px 4px -1px rgba(124, 58, 237, 0.25)',
                '&:hover': {
                  backgroundColor: '#6d28d9',
                  boxShadow: '0 4px 8px -2px rgba(124, 58, 237, 0.35)'
                }
              }}
            >
              {saving ? 'Saving...' : 'Save as Active Template'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Send Test Email Modal */}
      <Dialog
        open={testEmailOpen}
        onClose={() => setTestEmailOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2.5,
            boxShadow: '0 20px 40px -5px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ p: 0, pb: 2, fontWeight: 800, fontSize: '1.25rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon sx={{ color: '#7c3aed', fontSize: 22 }} /> Send Test Email
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>To:</Typography>
              {userDetails?.email && (
                <Button
                  size="small"
                  onClick={() => setTestEmailTo(userDetails.email)}
                  sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700, p: 0, minWidth: 'auto', color: '#7c3aed', '&:hover': { background: 'transparent', textDecoration: 'underline' } }}
                >
                  Send to Me
                </Button>
              )}
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. candidate@example.com"
              value={testEmailTo}
              onChange={(e) => setTestEmailTo(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              inputProps={{ style: { fontSize: '0.85rem' } }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', mb: 0.8 }}>CC:</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. hr@company.com"
              value={testEmailCc}
              onChange={(e) => setTestEmailCc(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              inputProps={{ style: { fontSize: '0.85rem' } }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', mb: 0.8 }}>Subject:</Typography>
            <TextField
              fullWidth
              size="small"
              value={testEmailSubject}
              onChange={(e) => setTestEmailSubject(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              inputProps={{ style: { fontSize: '0.85rem' } }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <input
                type="checkbox"
                id="includeChanges"
                checked={testEmailIncludeChanges}
                onChange={(e) => setTestEmailIncludeChanges(e.target.checked)}
                style={{ cursor: 'pointer', width: 16, height: 16, accentColor: '#7c3aed' }}
              />
              <label htmlFor="includeChanges" style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
                Include current template changes
              </label>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <input
                type="checkbox"
                id="useSampleData"
                checked={testEmailUseSampleData}
                onChange={(e) => setTestEmailUseSampleData(e.target.checked)}
                style={{ cursor: 'pointer', width: 16, height: 16, accentColor: '#7c3aed' }}
              />
              <label htmlFor="useSampleData" style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
                Use sample data
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 0, pt: 3.5, gap: 1.5 }}>
          <Button
            onClick={() => setTestEmailOpen(false)}
            variant="outlined"
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 2.5, py: 1, color: '#64748b', borderColor: '#cbd5e1' }}
          >
            Cancel
          </Button>
          <Button
            onClick={executeSendTestEmail}
            variant="contained"
            disabled={sendingTest}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 2.5, py: 1, backgroundColor: '#7c3aed', color: 'white', '&:hover': { backgroundColor: '#6d28d9' } }}
          >
            {sendingTest ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (embedded) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        {layout}
        {dialogs}
      </div>
    );
  }

  return (
    <PageShell className="bg-[#f8fafc]">
      <PageCard overflow={false}>
        <PageHeader
          title={t('settings.emailTemplates', 'Email Templates')}
          subtitle={t('settings.emailTemplatesSub', 'Customize and manage email communications for recruitment phases')}
        />
        <PageBody className="flex min-h-0 flex-1 flex-col overflow-hidden p-3 sm:p-4">
          {layout}
        </PageBody>
      </PageCard>
      {dialogs}
    </PageShell>
  );
};

export default EmailTemplates;
